import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Switch,
  Tooltip,
  FormControlLabel,
  Autocomplete,
  Avatar,
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  DragIndicator as DragIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Edit as EditIcon,
  AutoAwesome as AiIcon,
  Image as ImageIcon,
  Fullscreen as FullscreenIcon,
  AccountTree as TreeIcon,
} from '@mui/icons-material';
import PatternMindMap, { PatternMindMapRef } from '../../components/PatternMindMap';
import ThumbnailPicker from '../../components/ThumbnailPicker';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import patternService from '../../services/pattern.service';
import patternContainerService from '../../services/patternContainerService';
import courseService from '../../services/course.service';
import videoService from '../../services/video.service';
import authService from '../../services/auth.service';
import imageService, { Image } from '../../services/image.service';
import api from '../../services/api';
import { ExercisePattern, PatternContainer, Course, Video, Solution, Skill, CompositePattern, CompositePatternItem as CompositePatternItemType } from '../../types';
import compositePatternService from '../../services/compositePattern.service';
import { toast } from 'react-toastify';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';
import PatternChoiceContent from '../../components/PatternChoiceContent';
import PatternBrowser from '../../components/PatternBrowser';
import ParentDropZone from '../../components/ParentDropZone';
import PatternPdfViewer from '../../components/PatternPdfViewer';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import PdfDisplay from '../../components/PdfDisplay';
import PdfViewer from '../../components/PdfViewer';
import ThumbnailSelector from '../../components/ThumbnailSelector';
import PythonEditor from '../../components/PythonEditor';
import LatexEditor from '../../components/LatexEditor';
import QuickRecapBadge from '../../components/QuickRecapBadge';
import QuickRecapModal from '../../components/QuickRecapModal';
import useSkillStore from '../../store/skillStore';
import SolutionCard from '../../components/SolutionCard';
import solutionService from '../../services/solution.service';
import solutionContainerService from '../../services/solutionContainerService';
import skillService from '../../services/skill.service';
import SkillSelectionModal from '../../components/SkillSelectionModal';
import PatternStatusIndicator from '../../components/PatternStatusIndicator';
import PatternRedactionElementsSection from '../../components/PatternRedactionElementsSection';

// Sortable Item Component for patterns, containers and composite patterns
type ItemKind = 'pattern' | 'container' | 'composite';

interface SortableItemProps {
  item: ExercisePattern | PatternContainer | CompositePattern;
  itemKind?: ItemKind;
  isContainer?: boolean; // kept for backward compat
  isSelected: boolean;
  onSelect: (item: ExercisePattern | PatternContainer | CompositePattern, isContainer: boolean, kind?: ItemKind) => void;
  onDeleteContainer?: (container: PatternContainer) => void;
  onDeletePattern?: (pattern: ExercisePattern) => void;
  onDeleteComposite?: (cp: CompositePattern) => void;
  onRenameItem?: (item: ExercisePattern | PatternContainer | CompositePattern, isContainer: boolean, kind?: ItemKind) => void;
  onQuickRecapClick?: (pattern: ExercisePattern) => void;
  onTogglePublic?: (pattern: ExercisePattern, isPublic: boolean) => void;
  showDescription?: boolean;
}

const SortableItem: React.FC<SortableItemProps & { activeItemType: 'container' | 'pattern' | null }> = ({
  item,
  itemKind,
  isContainer: isContainerProp = false,
  isSelected,
  onSelect,
  onDeleteContainer,
  onDeletePattern,
  onDeleteComposite,
  onRenameItem,
  onQuickRecapClick,
  onTogglePublic,
  activeItemType,
  showDescription
}) => {
  const kind: ItemKind = itemKind || (isContainerProp ? 'container' : 'pattern');
  const isContainer = kind === 'container';
  const isComposite = kind === 'composite';
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: `${kind === 'container' ? 'container' : kind === 'composite' ? 'composite' : 'pattern'}-${item.id}` });

  const style = {
    transform: (isContainer && activeItemType === 'pattern') ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Check if item is public
  const isPublic = !isContainer && !isComposite && 'is_public' in item && item.is_public;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: 'pointer',
        backgroundColor: isSelected
          ? 'action.selected'
          : (isContainer && isOver)
            ? 'primary.light'
            : (isContainer && (item as PatternContainer).all_have_solutions)
              ? 'rgba(76, 175, 80, 0.08)'
              : isComposite
                ? 'rgba(156, 39, 176, 0.04)'
                : isPublic
                  ? 'rgba(25, 118, 210, 0.05)'
                  : 'inherit',
        position: 'relative',
        border: isContainer ? '2px dashed' : isComposite ? '1px solid' : '1px solid',
        borderColor: isContainer ? (isOver ? 'primary.main' : 'primary.light') : isComposite ? 'secondary.main' : 'divider',
        transform: isContainer && isOver ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.2s ease',
        boxShadow: isContainer && isOver ? '0 4px 12px rgba(25, 118, 210, 0.25)' : 'none',
      }}
      onClick={() => onSelect(item, isContainer, kind)}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            {isContainer ? (
              <DragIcon sx={{ fontSize: 20, color: 'action.disabled' }} />
            ) : isComposite ? (
              <DragIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
            ) : (
              <PatternStatusIndicator
                hasSolution={(item as ExercisePattern).has_solution || false}
                hasLatexSolution={(item as ExercisePattern).has_latex_solution || false}
                hasSkillSolution={(item as ExercisePattern).has_skill_solution || false}
                isStatic={!(item as ExercisePattern).is_reusable}
                isReferencePattern={(item as ExercisePattern).is_reference_pattern || false}
                hasPublicSolution={(item as ExercisePattern).has_public_solution || false}
                sx={{ fontSize: 20 }}
              />
            )}
          </Box>
          <Box flex="1 1 auto" display="flex" alignItems="center" gap={1} minWidth={0}>
            {isContainer ? (
              (item as any).thumbnail_url ? (
                <Avatar src={(item as any).thumbnail_url} variant="rounded" sx={{ width: 40, height: 40 }} />
              ) : (
                <FolderIcon sx={{ color: 'action.active' }} />
              )
            ) : isComposite ? (
              <TreeIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
            ) : (
              <FileIcon sx={{ color: 'action.disabled', fontSize: 20 }} />
            )}
            <Box minWidth={0} flex={1} sx={{ py: 0.5 }}>
              <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>{item.title}</Typography>
              {isContainer && 'children_count' in item && (
                <Typography variant="caption" color="textSecondary">
                  {item.children_count} contenants, {item.patterns_count} patterns
                </Typography>
              )}
              {isComposite && (
                <Typography variant="caption" color="secondary">
                  {(item as CompositePattern).child_count} pattern{(item as CompositePattern).child_count > 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end" flexShrink={0}>
            {/* Première ligne : QuickRecap et Public/Privé */}
            <Box display="flex" gap={0.5} alignItems="center">
              {!isContainer && !isComposite && onQuickRecapClick && (
                <QuickRecapBadge
                  hasCourse={!!(item as ExercisePattern).quick_recap_course}
                  hasVideo={!!(item as ExercisePattern).quick_recap_video}
                  onClick={() => onQuickRecapClick(item as ExercisePattern)}
                  size="small"
                />
              )}
              {!isContainer && !isComposite && onTogglePublic && (
                <Switch
                  size="small"
                  checked={isPublic}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    onTogglePublic(item as ExercisePattern, e.target.checked);
                  }}
                />
              )}
            </Box>
            {/* Deuxième ligne : Edit et Delete */}
            <Box display="flex" gap={0.5} alignItems="center">
              {onRenameItem && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRenameItem(item, isContainer, kind);
                  }}
                  sx={{ color: 'action.active' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {isContainer && onDeleteContainer && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteContainer(item as PatternContainer);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {!isContainer && !isComposite && onDeletePattern && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeletePattern(item as ExercisePattern);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {isComposite && onDeleteComposite && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteComposite(item as CompositePattern);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
        {!isContainer && !isComposite && 'original_author_name' in item && item.original_author_name && (
          <Typography variant="caption" color="primary" display="block">
            Auteur original: {item.original_author_name}
          </Typography>
        )}
        {isContainer && showDescription && (item as PatternContainer).description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 5 }}>
            {(item as PatternContainer).description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Per-pattern draft entry for isolated editing (prevents cross-contamination on quick switch)
interface PatternDraft {
  serverSnapshot: ExercisePattern;
  title: string;
  latexTemplate: string;
  pythonGenerator: string;
  tags: string[];
  isReusable: boolean;
  isPublic: boolean;
  generationResults: any[] | null;
  loadedAt: number;
}

interface ExercisePatternsProps {
  navigateToPattern?: { patternId: number; containerId: number | null } | null;
  onNavigationHandled?: () => void;
  urlContainerId?: number | null;
  urlItemId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const ExercisePatterns: React.FC<ExercisePatternsProps> = ({ navigateToPattern, onNavigationHandled, urlContainerId, urlItemId, onNavChange }) => {
  const [patterns, setPatterns] = useState<ExercisePattern[]>([]);
  const [containers, setContainers] = useState<PatternContainer[]>([]);
  const [compositePatterns, setCompositePatterns] = useState<CompositePattern[]>([]);
  const [selectedCompositePattern, setSelectedCompositePattern] = useState<CompositePattern | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);

  // --- Draft cache: per-pattern isolated state ---
  const [activePatternId, setActivePatternId] = useState<number | null>(null);
  const [draftCache, setDraftCache] = useState<Map<number, PatternDraft>>(new Map());
  const generationRef = useRef(0);
  const draftCacheRef = useRef<Map<number, PatternDraft>>(new Map());
  const activePatternIdRef = useRef<number | null>(null);
  const [loadingPatternId, setLoadingPatternId] = useState<number | null>(null);

  useEffect(() => { draftCacheRef.current = draftCache; }, [draftCache]);
  useEffect(() => { activePatternIdRef.current = activePatternId; }, [activePatternId]);

  // Derived values from active draft (backward-compatible)
  const activeDraft = activePatternId !== null ? draftCache.get(activePatternId) : undefined;
  const selectedPattern = activeDraft?.serverSnapshot ?? null;
  const patternTitle = activeDraft?.title ?? '';
  const latexTemplate = activeDraft?.latexTemplate ?? '';
  const pythonGenerator = activeDraft?.pythonGenerator ?? '';
  const tags = activeDraft?.tags ?? [];
  const isReusable = activeDraft?.isReusable ?? true;
  const isPublic = activeDraft?.isPublic ?? false;
  const generationResults = activeDraft?.generationResults ?? null;

  // Setter wrappers (same signatures as old useState setters)
  const updateActiveDraft = useCallback(<K extends keyof PatternDraft>(field: K, value: PatternDraft[K]) => {
    setDraftCache(prev => {
      const id = activePatternIdRef.current;
      if (id === null) return prev;
      const draft = prev.get(id);
      if (!draft) return prev;
      const next = new Map(prev);
      next.set(id, { ...draft, [field]: value });
      return next;
    });
  }, []);

  const setPatternTitle = useCallback((v: string) => updateActiveDraft('title', v), [updateActiveDraft]);
  const setLatexTemplate = useCallback((v: string) => updateActiveDraft('latexTemplate', v), [updateActiveDraft]);
  const setPythonGenerator = useCallback((v: string) => updateActiveDraft('pythonGenerator', v), [updateActiveDraft]);
  const setTags = useCallback((v: string[]) => updateActiveDraft('tags', v), [updateActiveDraft]);
  const setIsReusable = useCallback((v: boolean) => updateActiveDraft('isReusable', v), [updateActiveDraft]);
  const setIsPublic = useCallback((v: boolean) => updateActiveDraft('isPublic', v), [updateActiveDraft]);
  const setGenerationResults = useCallback((v: any[] | null) => updateActiveDraft('generationResults', v as PatternDraft['generationResults']), [updateActiveDraft]);

  // setSelectedPattern wrapper: updates serverSnapshot in draft (does NOT switch active pattern)
  const setSelectedPattern = useCallback((patternOrNull: ExercisePattern | null) => {
    if (patternOrNull === null) {
      setActivePatternId(null);
      return;
    }
    setDraftCache(prev => {
      const existing = prev.get(patternOrNull.id);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(patternOrNull.id, { ...existing, serverSnapshot: patternOrNull });
      return next;
    });
  }, []);

  const [currentContainer, setCurrentContainer] = useState<PatternContainer | null>(null);
  const [navigationPath, setNavigationPath] = useState<PatternContainer[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewKey, setPdfPreviewKey] = useState(0);
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'pattern' | 'container' | 'existing' | 'composite'>('pattern');
  const [containerTitle, setContainerTitle] = useState('');
  const [containerDescription, setContainerDescription] = useState('');
  const [editContainerDescription, setEditContainerDescription] = useState('');
  const [editContainerThumbnail, setEditContainerThumbnail] = useState<number | null>(null);
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [newPatternTitle, setNewPatternTitle] = useState('');
  const [activeItemType, setActiveItemType] = useState<'container' | 'pattern' | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState<{item: ExercisePattern | PatternContainer | CompositePattern, isContainer: boolean, kind?: ItemKind} | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [generationLoading, setGenerationLoading] = useState(false);
  const [thumbnailSelectorOpen, setThumbnailSelectorOpen] = useState(false);
  const [patternToDelete, setPatternToDelete] = useState<ExercisePattern | null>(null);

  // AI Assistant states for LaTeX
  const [aiLatexAssistantOpen, setAiLatexAssistantOpen] = useState(false);
  const [aiLatexPrompt, setAiLatexPrompt] = useState('');
  const [aiLatexContent, setAiLatexContent] = useState('');
  const [aiLatexLoading, setAiLatexLoading] = useState(false);
  const [aiLatexPdfKey, setAiLatexPdfKey] = useState(0);

  // AI Assistant states for Python
  const [aiPythonAssistantOpen, setAiPythonAssistantOpen] = useState(false);
  const [aiPythonPrompt, setAiPythonPrompt] = useState('');
  const [aiPythonContent, setAiPythonContent] = useState('');
  const [aiPythonLoading, setAiPythonLoading] = useState(false);
  const [aiPythonPdfKey, setAiPythonPdfKey] = useState(0); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Fullscreen Python editor state
  const [fullscreenPythonEditorOpen, setFullscreenPythonEditorOpen] = useState(false);
  const [fullscreenTestResults, setFullscreenTestResults] = useState<any[] | null>(null);
  const [fullscreenTestLoading, setFullscreenTestLoading] = useState(false);

  // Fullscreen LaTeX editor state
  const [fullscreenLatexEditorOpen, setFullscreenLatexEditorOpen] = useState(false);
  const [editorWidth, setEditorWidth] = useState(66); // Percentage width for the editor

  // AI Python assistant test results
  const [aiPythonTestResults, setAiPythonTestResults] = useState<any[] | null>(null);
  const [aiPythonTestLoading, setAiPythonTestLoading] = useState(false);

  // Tree view modal state
  const [treeViewOpen, setTreeViewOpen] = useState(false);
  const [allContainersForTree, setAllContainersForTree] = useState<PatternContainer[]>([]);
  const [allPatternsForTree, setAllPatternsForTree] = useState<ExercisePattern[]>([]);
  const [treeSearchQuery, setTreeSearchQuery] = useState('');
  const [debouncedTreeSearchQuery, setDebouncedTreeSearchQuery] = useState('');
  const [isExpandModeActive, setIsExpandModeActive] = useState(false);
  const [isCollapseSiblingsModeActive, setIsCollapseSiblingsModeActive] = useState(false);
  const mindMapRef = useRef<PatternMindMapRef>(null);

  // Composite pattern creation/edit state
  const [compositeTitle, setCompositeTitle] = useState('');
  const [compositeChildPatterns, setCompositeChildPatterns] = useState<ExercisePattern[]>([]);
  const [compositeSearchResults, setCompositeSearchResults] = useState<ExercisePattern[]>([]);
  const [compositeSearchLoading, setCompositeSearchLoading] = useState(false);
  const [compositeTreeOpen, setCompositeTreeOpen] = useState(false);
  const [compositeTreePatterns, setCompositeTreePatterns] = useState<ExercisePattern[]>([]);
  const [compositeTreeContainers, setCompositeTreeContainers] = useState<PatternContainer[]>([]);
  const [compositeTreeSearch, setCompositeTreeSearch] = useState('');
  const compositeTreeRef = useRef<PatternMindMapRef>(null);
  const [editingCompositeId, setEditingCompositeId] = useState<number | null>(null);

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    const currentId = currentContainer?.id ?? null;
    if (urlContainerId !== undefined && urlContainerId !== currentId) {
      if (urlContainerId === null) {
        setCurrentContainer(null);
        setNavigationPath([]);
      } else {
        const restoreContainer = async () => {
          try {
            const target: PatternContainer = await patternContainerService.getContainer(urlContainerId);
            const path: PatternContainer[] = [];
            let cId: number | null = target.parent ?? null;
            while (cId) {
              const c: PatternContainer = await patternContainerService.getContainer(cId);
              path.unshift(c);
              cId = c.parent ?? null;
            }
            path.push(target);
            setNavigationPath(path);
            setCurrentContainer(target);
          } catch (err) {
            console.error('Error restoring container from URL:', err);
          }
        };
        restoreContainer();
      }
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (urlItemId !== undefined && urlItemId !== activePatternId) {
      setActivePatternId(urlItemId);
    }
  }, [urlItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce tree search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTreeSearchQuery(treeSearchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [treeSearchQuery]);

  // Image management states
  const [allImages, setAllImages] = useState<Image[]>([]);
  const [imageSelectionDialogOpen, setImageSelectionDialogOpen] = useState(false);
  const [quickRecapModalOpen, setQuickRecapModalOpen] = useState(false);
  const [quickRecapPattern, setQuickRecapPattern] = useState<ExercisePattern | null>(null);
  const [patternSolutions, setPatternSolutions] = useState<Solution[]>([]);
  const [solutionsLoading, setSolutionsLoading] = useState(false);
  const [createSolutionDialogOpen, setCreateSolutionDialogOpen] = useState(false);
  const [solutionContainers, setSolutionContainers] = useState<any[]>([]);
  const [selectedSolutionContainer, setSelectedSolutionContainer] = useState<any | null>(null);

  // Solution editing modal states
  const [editSolutionModalOpen, setEditSolutionModalOpen] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
  const [solutionLatexTemplate, setSolutionLatexTemplate] = useState('');
  const [solutionPythonFunction, setSolutionPythonFunction] = useState('');
  const [solutionTags, setSolutionTags] = useState<string[]>([]);
  const [solutionTagInput, setSolutionTagInput] = useState('');
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [skillSelectionModalOpen, setSkillSelectionModalOpen] = useState(false);
  const [skillsWithWeights, setSkillsWithWeights] = useState<Array<{ skill_id: number; weight: number }>>([]);
  const [solutionPdfKey, setSolutionPdfKey] = useState(0);

  // Check if current user is the author of the pattern
  const isUserAuthor = useCallback((pattern: ExercisePattern | null): boolean => {
    if (!pattern) return false;
    const currentUser = authService.getCurrentUser();
    if (!currentUser) return false;

    // User is author if they created it OR they are the original author
    return pattern.created_by === currentUser.id || pattern.original_author === currentUser.id;
  }, []);

  // Centralized save helper: saves a specific pattern's draft by ID (reads from ref, not state)
  const saveDraftIfDirty = useCallback(async (patternId: number | null) => {
    if (patternId === null) return;
    const draft = draftCacheRef.current.get(patternId);
    if (!draft) return;
    const snapshot = draft.serverSnapshot;
    if (snapshot.is_public && !isUserAuthor(snapshot)) return;

    const isDirty = (
      draft.title !== (snapshot.title || '') ||
      draft.latexTemplate !== (snapshot.latex_template || '') ||
      draft.pythonGenerator !== (snapshot.python_generator || '') ||
      JSON.stringify(draft.tags) !== JSON.stringify(snapshot.tags || []) ||
      draft.isReusable !== snapshot.is_reusable ||
      draft.isPublic !== snapshot.is_public
    );
    if (!isDirty) return;

    try {
      const patternData = {
        title: draft.title || 'Nouveau Pattern',
        latex_template: draft.latexTemplate,
        python_generator: draft.pythonGenerator,
        tags: draft.tags,
        is_reusable: draft.isReusable,
        is_public: draft.isPublic,
      };
      const updated = await patternService.updatePattern(patternId, patternData);
      // Mark draft as clean
      setDraftCache(prev => {
        const current = prev.get(patternId);
        if (!current) return prev;
        const next = new Map(prev);
        next.set(patternId, { ...current, serverSnapshot: updated });
        return next;
      });
      setPatterns(prev => prev.map(p => p.id === patternId ? updated : p));
    } catch (error) {
      console.error('Error saving pattern:', error);
    }
  }, [isUserAuthor]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const initialLoadDone = useRef(false);

  const loadItems = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);

      if (currentContainer) {
        // Load contents of current container + composite patterns
        const [contents, composites] = await Promise.all([
          patternContainerService.getContainerContents(currentContainer.id),
          compositePatternService.getAll({ container: currentContainer.id }),
        ]);
        setContainers(Array.isArray(contents?.containers) ? contents.containers : []);
        setPatterns(Array.isArray(contents?.patterns) ? contents.patterns : []);
        setCompositePatterns(composites);
      } else {
        // Load root level items
        const [containerData, patternData, composites] = await Promise.all([
          patternContainerService.getContainers(null),
          patternService.getPatterns(null),
          compositePatternService.getAll({ container: null }),
        ]);

        // Ensure data is arrays
        const validContainerData = Array.isArray(containerData) ? containerData : [];
        const validPatternData = Array.isArray(patternData) ? patternData : [];

        setContainers(validContainerData);
        setPatterns(validPatternData);
        setCompositePatterns(composites);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des éléments');
      setPatterns([]);
      setContainers([]);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [currentContainer]);

  // Load courses and videos once (for quick recap selection)
  const coursesLoadedRef = useRef(false);
  useEffect(() => {
    if (coursesLoadedRef.current) return;
    coursesLoadedRef.current = true;
    Promise.all([
      courseService.getCourses(),
      videoService.getAll()
    ]).then(([courseData, videoData]) => {
      setCourses(Array.isArray(courseData) ? courseData : []);
      setVideos(Array.isArray(videoData) ? videoData.filter(v => !v.is_deleted) : []);
    }).catch(() => {
      setCourses([]);
      setVideos([]);
    });
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Handle external navigation to a specific pattern
  useEffect(() => {
    if (!navigateToPattern) return;
    const { patternId, containerId } = navigateToPattern;

    const doNavigate = async () => {
      try {
        // Build container path if pattern is in a container
        if (containerId) {
          const path: PatternContainer[] = [];
          let cId: number | null = containerId;
          while (cId) {
            const c: PatternContainer = await patternContainerService.getContainer(cId);
            path.unshift(c);
            cId = c.parent ?? null;
          }
          setNavigationPath(path);
          setCurrentContainer(path[path.length - 1]);
        } else {
          setNavigationPath([]);
          setCurrentContainer(null);
        }

        // After container loads, select the pattern
        setTimeout(async () => {
          try {
            const pattern = await patternService.getPattern(patternId);
            setActivePatternId(patternId);
            setDraftCache(prev => {
              const next = new Map(prev);
              next.set(patternId, {
                serverSnapshot: pattern,
                title: pattern.title,
                latexTemplate: pattern.latex_template || '',
                pythonGenerator: pattern.python_generator || '',
                tags: pattern.tags || [],
                isReusable: pattern.is_reusable ?? true,
                isPublic: pattern.is_public ?? false,
                generationResults: null,
                loadedAt: Date.now(),
              });
              return next;
            });
          } catch (e) {
            console.error('Error navigating to pattern:', e);
          }
        }, 300);
      } catch (e) {
        console.error('Error building container path:', e);
      }
      onNavigationHandled?.();
    };

    doNavigate();
  }, [navigateToPattern]);

  // Register callback to save before tab change
  useEffect(() => {
    registerBeforeTabChange('patterns', () => saveDraftIfDirty(activePatternIdRef.current));
    return () => { unregisterBeforeTabChange('patterns'); };
  }, [saveDraftIfDirty]);

  // Save on component unmount
  useEffect(() => {
    return () => {
      const patternId = activePatternIdRef.current;
      if (patternId === null) return;
      const draft = draftCacheRef.current.get(patternId);
      if (!draft) return;
      const snapshot = draft.serverSnapshot;
      if (snapshot.is_public && !isUserAuthor(snapshot)) return;
      const isDirty = (
        draft.title !== (snapshot.title || '') ||
        draft.latexTemplate !== (snapshot.latex_template || '') ||
        draft.pythonGenerator !== (snapshot.python_generator || '') ||
        JSON.stringify(draft.tags) !== JSON.stringify(snapshot.tags || []) ||
        draft.isReusable !== snapshot.is_reusable ||
        draft.isPublic !== snapshot.is_public
      );
      if (!isDirty) return;
      patternService.updatePattern(patternId, {
        title: draft.title || 'Nouveau Pattern',
        latex_template: draft.latexTemplate,
        python_generator: draft.pythonGenerator,
        tags: draft.tags,
        is_reusable: draft.isReusable,
        is_public: draft.isPublic,
      }).catch(err => console.error('Error saving pattern on unmount:', err));
    };
  }, [isUserAuthor]);

  // Auto-save: debounced 3s after last edit
  useEffect(() => {
    if (activePatternId === null) return;
    const timer = setTimeout(() => {
      saveDraftIfDirty(activePatternId);
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePatternId, patternTitle, latexTemplate, pythonGenerator, tags, isReusable, isPublic, saveDraftIfDirty]);

  // Auto-refresh PDF preview when latexTemplate or pythonGenerator changes
  useEffect(() => {
    if (showPdfPreview && selectedPattern && (latexTemplate || pythonGenerator)) {
      const timeoutId = setTimeout(() => {
        setPdfPreviewKey(prev => prev + 1);
      }, 2500); // Slightly longer than auto-save to ensure save completes first

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latexTemplate, pythonGenerator, showPdfPreview]); // Removed selectedPattern to prevent loop when pattern is reloaded after PDF generation

  // Load solutions when active pattern changes
  useEffect(() => {
    const loadSolutions = async () => {
      if (activePatternId) {
        setSolutionsLoading(true);
        try {
          const solutions = await solutionService.getSolutionsByPattern(activePatternId);
          setPatternSolutions(solutions);
        } catch (error) {
          console.error('Error loading solutions:', error);
          setPatternSolutions([]);
        } finally {
          setSolutionsLoading(false);
        }
      } else {
        setPatternSolutions([]);
      }
    };

    loadSolutions();
  }, [activePatternId]);

  // Load all skills on mount
  useEffect(() => {
    const loadSkills = async () => {
      try {
        const skills = await skillService.getAll();
        setAllSkills(skills);
      } catch (error) {
        console.error('Error loading skills:', error);
      }
    };

    loadSkills();
  }, []);

  // Update solution modal fields when solution is selected
  useEffect(() => {
    if (selectedSolution) {
      setSolutionLatexTemplate(selectedSolution.latex_template || '');
      setSolutionPythonFunction(selectedSolution.python_function || '');
      setSolutionTags(selectedSolution.tags || []);
      setSelectedSkills(selectedSolution.skills || []);

      // Initialize skills with weights from solution_skills
      if (selectedSolution.solution_skills) {
        const initialSkillsWithWeights = selectedSolution.solution_skills.map((ss: any) => ({
          skill_id: ss.skill.id,
          weight: ss.weight
        }));
        setSkillsWithWeights(initialSkillsWithWeights);
      } else {
        setSkillsWithWeights([]);
      }
    }
  }, [selectedSolution]);

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id.toString();
    if (activeId.startsWith('container-')) {
      setActiveItemType('container');
    } else if (activeId.startsWith('pattern-') || activeId.startsWith('composite-')) {
      setActiveItemType('pattern');
    }
  };

  const handleDropToParent = async (itemId: string) => {
    const parentId = navigationPath.length > 1
      ? navigationPath[navigationPath.length - 2].id
      : null;

    if (itemId.startsWith('pattern-')) {
      const patternId = parseInt(itemId.replace('pattern-', ''));
      const pattern = patterns.find(p => p.id === patternId);
      if (pattern) {
        try {
          // For public patterns, only update container
          const updateData = pattern.is_public
            ? { container: parentId }
            : { ...pattern, container: parentId };

          await patternService.updatePattern(patternId, updateData);
          toast.success(`Pattern "${pattern.title}" déplacé vers le niveau parent`);
          await loadItems();
        } catch (error) {
          toast.error('Erreur lors du déplacement');
        }
      }
    } else if (itemId.startsWith('composite-')) {
      const compositeId = parseInt(itemId.replace('composite-', ''));
      const cp = compositePatterns.find(c => c.id === compositeId);
      if (cp) {
        try {
          await compositePatternService.update(compositeId, { container: parentId });
          toast.success(`Pattern composé "${cp.title}" déplacé vers le niveau parent`);
          await loadItems();
        } catch (error) {
          toast.error('Erreur lors du déplacement');
        }
      }
    } else if (itemId.startsWith('container-')) {
      const containerId = parseInt(itemId.replace('container-', ''));
      const container = containers.find(c => c.id === containerId);
      if (container) {
        try {
          await patternContainerService.updateContainer(containerId, {
            ...container,
            parent: parentId
          });
          toast.success(`Contenant "${container.title}" déplacé vers le niveau parent`);
          await loadItems();
        } catch (error) {
          toast.error('Erreur lors du déplacement du contenant');
        }
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveItemType(null);
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeId = active.id.toString();
      const overId = over.id.toString();

      const isActiveContainer = activeId.startsWith('container-');
      const isOverContainer = overId.startsWith('container-');
      const isActivePattern = activeId.startsWith('pattern-');
      const isActiveComposite = activeId.startsWith('composite-');
      const isOverParent = overId === 'parent-folder';

      // Case 1: Dropping onto parent folder (..) zone
      if (isOverParent) {
        await handleDropToParent(activeId);
        return;
      }

      // Case 2: Container dropped on another container
      if (isActiveContainer && isOverContainer) {
        const activeContainerId = parseInt(activeId.replace('container-', ''));
        const overContainerId = parseInt(overId.replace('container-', ''));
        if (activeContainerId === overContainerId) return;
      }

      // Case 3: Dropping a pattern or composite onto a container - move into container
      if ((isActivePattern || isActiveComposite) && isOverContainer) {
        const containerId = parseInt(overId.replace('container-', ''));

        if (isActivePattern) {
          const patternId = parseInt(activeId.replace('pattern-', ''));
          const pattern = patterns.find(p => p.id === patternId);
          if (pattern) {
            try {
              const updateData = pattern.is_public
                ? { container: containerId }
                : { ...pattern, container: containerId };
              await patternService.updatePattern(patternId, updateData);
              toast.success(`Pattern "${pattern.title}" déplacé dans le contenant`);
              await loadItems();
            } catch (error) {
              toast.error('Erreur lors du déplacement du pattern');
            }
          }
        } else {
          const compositeId = parseInt(activeId.replace('composite-', ''));
          const cp = compositePatterns.find(c => c.id === compositeId);
          if (cp) {
            try {
              await compositePatternService.update(compositeId, { container: containerId });
              toast.success(`Pattern composé "${cp.title}" déplacé dans le contenant`);
              await loadItems();
            } catch (error) {
              toast.error('Erreur lors du déplacement');
            }
          }
        }
        return;
      }

      // Case 4: Regular reordering within the same level
      // Helper to determine item type from the mixed array
      const getItemPrefix = (item: any): string => {
        if ('children_count' in item) return 'container';
        if ('child_count' in item) return 'composite';
        return 'pattern';
      };

      const allItems: any[] = [...containers, ...compositePatterns, ...patterns];
      const findIndex = (id: string) => allItems.findIndex(item => {
        const prefix = getItemPrefix(item);
        return `${prefix}-${item.id}` === id;
      });

      const activeIndex = findIndex(activeId);
      const overIndex = findIndex(overId);

      if (activeIndex !== -1 && overIndex !== -1) {
        const newItems = arrayMove(allItems, activeIndex, overIndex);

        const newContainers = newItems.filter((item: any) => getItemPrefix(item) === 'container') as PatternContainer[];
        const newComposites = newItems.filter((item: any) => getItemPrefix(item) === 'composite') as CompositePattern[];
        const newPatterns = newItems.filter((item: any) => getItemPrefix(item) === 'pattern') as ExercisePattern[];

        setContainers(newContainers);
        setCompositePatterns(newComposites);
        setPatterns(newPatterns);

        const containerOrder = newContainers.map((item, index) => ({ id: item.id, order: index }));
        const compositeOrder = newComposites.map((item, index) => ({ id: item.id, order: index + newContainers.length }));
        const patternOrder = newPatterns.map((item, index) => ({ id: item.id, order: index + newContainers.length + newComposites.length }));

        if (containerOrder.length > 0) {
          patternContainerService.reorderContainers(containerOrder, currentContainer?.id).catch(() => loadItems());
        }
        if (compositeOrder.length > 0) {
          compositePatternService.reorder(compositeOrder).catch(() => loadItems());
        }
        if (patternOrder.length > 0) {
          patternService.reorderPatterns(patternOrder, currentContainer?.id).catch(() => loadItems());
        }
      }
    }
  };

  const handlePatternSelect = async (item: ExercisePattern | PatternContainer | CompositePattern, isContainer: boolean, kind?: ItemKind) => {
    // Increment generation counter to cancel stale fetches
    generationRef.current += 1;
    const thisGeneration = generationRef.current;

    // Fire-and-forget save of current draft (no await — instant switch)
    saveDraftIfDirty(activePatternIdRef.current);

    if (kind === 'composite') {
      const cp = item as CompositePattern;
      setActivePatternId(null);
      setSelectedCompositePattern(cp);
      setShowPdfPreview(false);
      // Load full composite with items
      try {
        const full = await compositePatternService.get(cp.id);
        if (generationRef.current !== thisGeneration) return;
        setSelectedCompositePattern(full);
      } catch {
        // keep partial data
      }
      return;
    }

    setSelectedCompositePattern(null);

    if (isContainer) {
      const container = item as PatternContainer;
      setActivePatternId(null);
      setNavigationPath([...navigationPath, container]);
      setCurrentContainer(container);
      setShowPdfPreview(false);
      onNavChange?.(container.id, null, container.title);
    } else {
      const pattern = item as ExercisePattern;
      const newPatternId = pattern.id;

      // Switch immediately — if draft exists, editor displays it instantly
      setActivePatternId(newPatternId);
      setShowPdfPreview(false);
      onNavChange?.(currentContainer?.id ?? null, newPatternId, pattern.title);

      // If no draft yet, show loading state
      if (!draftCache.has(newPatternId)) {
        setLoadingPatternId(newPatternId);
      }

      // Fetch full pattern data from server (list items don't have latex_template)
      try {
        const freshPattern = await patternService.getPattern(newPatternId);

        // Stale check: if user switched away, discard
        if (generationRef.current !== thisGeneration) return;

        setDraftCache(prev => {
          const next = new Map(prev);
          const existing = next.get(newPatternId);
          if (existing) {
            // Draft exists (user had edited before) — keep edits, refresh snapshot
            next.set(newPatternId, { ...existing, serverSnapshot: freshPattern });
          } else {
            // First load — populate from server
            next.set(newPatternId, {
              serverSnapshot: freshPattern,
              title: freshPattern.title,
              latexTemplate: freshPattern.latex_template,
              pythonGenerator: freshPattern.python_generator,
              tags: freshPattern.tags || [],
              isReusable: freshPattern.is_reusable ?? true,
              isPublic: freshPattern.is_public ?? false,
              generationResults: freshPattern.last_test_values || null,
              loadedAt: Date.now(),
            });
          }
          return next;
        });
      } catch (error) {
        console.error('Error fetching fresh pattern data:', error);
        if (generationRef.current !== thisGeneration) return;

        // Fallback: create draft from list item (partial data)
        setDraftCache(prev => {
          if (prev.has(newPatternId)) return prev;
          const next = new Map(prev);
          next.set(newPatternId, {
            serverSnapshot: pattern,
            title: pattern.title || '',
            latexTemplate: pattern.latex_template || '',
            pythonGenerator: pattern.python_generator || '',
            tags: pattern.tags || [],
            isReusable: pattern.is_reusable ?? true,
            isPublic: pattern.is_public ?? false,
            generationResults: pattern.last_test_values || null,
            loadedAt: Date.now(),
          });
          return next;
        });
      } finally {
        if (generationRef.current === thisGeneration) {
          setLoadingPatternId(null);
        }
      }
    }
  };

  const handleNavigateBack = () => {
    if (navigationPath.length > 0) {
      const newPath = [...navigationPath];
      newPath.pop();
      setNavigationPath(newPath);
      const newContainer = newPath[newPath.length - 1] || null;
      setCurrentContainer(newContainer);
      onNavChange?.(newContainer?.id ?? null, null, newContainer?.title);
    }
  };

  const handleRenameItem = async (item: ExercisePattern | PatternContainer | CompositePattern, isContainer: boolean, kind?: ItemKind) => {
    setItemToRename({ item, isContainer, kind });
    setRenameTitle(item.title);
    if (isContainer) {
      setEditContainerDescription((item as PatternContainer).description || '');
      setEditContainerThumbnail((item as PatternContainer).thumbnail || null);
    }
    setRenameDialogOpen(true);
  };

  const handleTogglePublic = async (pattern: ExercisePattern, newIsPublic: boolean) => {
    try {
      await patternService.updatePattern(pattern.id, {
        is_public: newIsPublic
      });

      toast.success(`Pattern rendu ${newIsPublic ? 'public' : 'privé'}`);

      // Update in the list
      setPatterns(patterns.map(p => p.id === pattern.id ? { ...p, is_public: newIsPublic } : p));

      // Update selected pattern if it's the same
      if (selectedPattern?.id === pattern.id) {
        setSelectedPattern({ ...selectedPattern, is_public: newIsPublic });
        setIsPublic(newIsPublic);
      }
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleQuickRecapClick = (pattern: ExercisePattern) => {
    setQuickRecapPattern(pattern);
    setQuickRecapModalOpen(true);
  };

  const handleSaveQuickRecapCourse = async (courseId: number | null) => {
    if (!quickRecapPattern) return;

    await patternService.updatePattern(quickRecapPattern.id, {
      quick_recap_course: courseId
    });

    toast.success('Référence cours mise à jour');

    setQuickRecapPattern({
      ...quickRecapPattern,
      quick_recap_course: courseId,
      quick_recap_course_title: courses.find(c => c.id === courseId)?.title || null
    });

    if (selectedPattern?.id === quickRecapPattern.id) {
      setSelectedPattern({
        ...selectedPattern,
        quick_recap_course: courseId,
        quick_recap_course_title: courses.find(c => c.id === courseId)?.title || null
      });
    }

    loadItems();
  };

  const handleSaveQuickRecapVideo = async (videoId: number | null) => {
    if (!quickRecapPattern) return;

    await patternService.updatePattern(quickRecapPattern.id, {
      quick_recap_video: videoId
    });

    toast.success('Référence vidéo mise à jour');

    setQuickRecapPattern({
      ...quickRecapPattern,
      quick_recap_video: videoId,
      quick_recap_video_title: videos.find(v => v.id === videoId)?.title || null
    });

    if (selectedPattern?.id === quickRecapPattern.id) {
      setSelectedPattern({
        ...selectedPattern,
        quick_recap_video: videoId,
        quick_recap_video_title: videos.find(v => v.id === videoId)?.title || null
      });
    }

    loadItems();
  };

  const handleConfirmRename = async () => {
    if (!itemToRename || !renameTitle.trim()) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      if (itemToRename.kind === 'composite') {
        await compositePatternService.update(itemToRename.item.id, {
          title: renameTitle.trim()
        });
        // Update selected composite if it's the same
        if (selectedCompositePattern?.id === itemToRename.item.id) {
          setSelectedCompositePattern({ ...selectedCompositePattern, title: renameTitle.trim() });
        }
      } else if (itemToRename.isContainer) {
        await patternContainerService.updateContainer(itemToRename.item.id, {
          title: renameTitle.trim(),
          description: editContainerDescription,
          thumbnail: editContainerThumbnail,
        } as any);
      } else {
        await patternService.updatePattern(itemToRename.item.id, {
          title: renameTitle.trim()
        });
      }

      toast.success('Renommage effectué avec succès');
      loadItems();
      setRenameDialogOpen(false);
      setItemToRename(null);
      setRenameTitle('');
      setEditContainerDescription('');
    } catch (error: any) {
      toast.error('Erreur lors du renommage: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContainer = async (container: PatternContainer) => {
    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le contenant "${container.title}" ?\n` +
      `Son contenu (${container.children_count} contenants et ${container.patterns_count} patterns) sera déplacé vers le niveau parent.`
    );

    if (!confirmDelete) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      // Get parent ID (either current container's parent or null for root)
      const parentId = currentContainer?.id || null;

      // First, get the container's contents
      const contents = await patternContainerService.getContainerContents(container.id);

      // Move all child containers to parent level
      if (contents.containers && contents.containers.length > 0) {
        for (const childContainer of contents.containers) {
          await patternContainerService.updateContainer(childContainer.id, {
            ...childContainer,
            parent: parentId
          });
        }
      }

      // Move all patterns to parent level
      if (contents.patterns && contents.patterns.length > 0) {
        for (const pattern of contents.patterns) {
          // For public patterns, only update container
          const updateData = pattern.is_public
            ? { container: parentId }
            : { ...pattern, container: parentId };

          await patternService.updatePattern(pattern.id, updateData);
        }
      }

      // Delete the container
      await patternContainerService.deleteContainer(container.id);

      toast.success(`Contenant "${container.title}" supprimé. Son contenu a été déplacé vers le niveau parent.`);

      // Reload items
      await loadItems();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression du contenant');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPattern = () => {
    setCreateType('pattern'); // Default to pattern
    setContainerTitle('');
    setContainerDescription('');
    setPatternDialogOpen(true);
  };

  const handleCreateContainer = async () => {
    if (!containerTitle.trim()) {
      toast.error('Veuillez entrer un titre pour le contenant');
      return;
    }

    try {
      if (!initialLoadDone.current) setLoading(true);
      await patternContainerService.createContainer({
        title: containerTitle,
        description: containerDescription,
        parent: currentContainer?.id || null
      });
      toast.success('Contenant créé avec succès');
      setContainerTitle('');
      setContainerDescription('');
      setPatternDialogOpen(false);
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la création du contenant');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewPattern = async () => {
    if (!newPatternTitle.trim()) {
      toast.error('Veuillez entrer un titre pour le pattern');
      return;
    }

    try {
      // eslint-disable-next-line no-template-curly-in-string
      const defaultTemplate = '% Template LaTeX avec balises {{variable}}\\n\\\\begin{exercise}\\n  Calculer ${{a}} + {{b}}$.\\n\\\\end{exercise}';
      const defaultGenerator = `import random

def generate():
    """Génère les valeurs pour l'exercice"""
    return {
        'a': random.randint(1, 10),
        'b': random.randint(1, 10)
    }`;

      const newPattern = await patternService.createPattern({
        title: newPatternTitle,
        latex_template: defaultTemplate,
        python_generator: defaultGenerator,
        tags: [],
        is_reusable: true,
        container: currentContainer?.id || null,
      });

      toast.success('Pattern créé avec succès');

      setPatternDialogOpen(false);
      setNewPatternTitle('');
      setCreateType('pattern');

      await loadItems();

      if (newPattern) {
        handlePatternSelect(newPattern, false);
      }
    } catch (error) {
      console.error('Error creating pattern:', error);
      toast.error('Erreur lors de la création du pattern');
    }
  };

  const handlePatternChoiceSelect = async (pattern: ExercisePattern) => {
    await loadItems();
    handlePatternSelect(pattern, false);
  };

  const handlePreview = () => {
    setShowPdfPreview(true);
    setPdfPreviewKey(prev => prev + 1); // Force component remount
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleDeleteComposite = async (cp: CompositePattern) => {
    if (!window.confirm(`Supprimer le pattern composé "${cp.title}" ?`)) return;
    try {
      await compositePatternService.delete(cp.id);
      toast.success('Pattern composé supprimé');
      if (selectedCompositePattern?.id === cp.id) {
        setSelectedCompositePattern(null);
      }
      await loadItems();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeletePattern = (pattern: ExercisePattern) => {
    if (!pattern) return;
    setPatternToDelete(pattern);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (keepPublic: boolean) => {
    if (!patternToDelete) return;

    try {
      const deletedId = patternToDelete.id;
      const wasReference = patternToDelete.is_reference_pattern;
      await patternService.deletePattern(deletedId, keepPublic);
      toast.success('Pattern supprimé avec succès');

      // Remove pattern from skill store so reference_patterns_details stay in sync
      if (wasReference) {
        useSkillStore.getState().removePattern(deletedId);
      }

      // Clear selection and draft if we deleted the selected pattern
      if (activePatternId === patternToDelete.id) {
        setActivePatternId(null);
        setShowPdfPreview(false);
      }
      // Remove draft from cache
      setDraftCache(prev => {
        if (!prev.has(patternToDelete.id)) return prev;
        const next = new Map(prev);
        next.delete(patternToDelete.id);
        return next;
      });

      setPatternToDelete(null);
      setDeleteDialogOpen(false);
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression du pattern');
    }
  };

  const handleDuplicatePattern = async () => {
    if (!selectedPattern) return;

    try {
      if (!initialLoadDone.current) setLoading(true);
      const duplicatedPattern = await patternService.duplicatePattern(selectedPattern.id);
      toast.success('Pattern dupliqué avec succès');
      await loadItems();
      handlePatternSelect(duplicatedPattern, false);
    } catch (error) {
      toast.error('Erreur lors de la duplication du pattern');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSolutionClick = async () => {
    if (!selectedPattern) return;

    try {
      // Load all solution containers
      const containers = await solutionContainerService.getContainers(null);
      setSolutionContainers(containers);

      // Find or create the default container
      const DEFAULT_CONTAINER_NAME = "solutions créées à partir d'un pattern";
      let defaultContainer = containers.find(c => c.title === DEFAULT_CONTAINER_NAME);

      if (!defaultContainer) {
        defaultContainer = await solutionContainerService.createContainer({
          title: DEFAULT_CONTAINER_NAME,
          parent: null,
          order: 0
        });
        // Add it to the list
        setSolutionContainers([defaultContainer, ...containers]);
        toast.info('Container par défaut créé');
      }

      // Pre-select the default container
      setSelectedSolutionContainer(defaultContainer);
      setCreateSolutionDialogOpen(true);
    } catch (error) {
      console.error('Error loading containers:', error);
      toast.error('Erreur lors du chargement des containers');
    }
  };

  const handleCreateSolution = async () => {
    if (!selectedPattern) {
      toast.error('Veuillez sélectionner un pattern');
      return;
    }

    if (!selectedSolutionContainer) {
      toast.error('Veuillez sélectionner un container');
      return;
    }

    try {
      if (!initialLoadDone.current) setLoading(true);

      await solutionService.createSolution({
        pattern: selectedPattern.id,
        latex_template: '',
        python_function: '',
        tags: [],
        container: selectedSolutionContainer.id,
      });
      toast.success('Solution créée avec succès');
      setCreateSolutionDialogOpen(false);

      // Reload solutions to show the new one
      const solutions = await solutionService.getSolutionsByPattern(selectedPattern.id);
      setPatternSolutions(solutions);
    } catch (error) {
      console.error('Error creating solution:', error);
      toast.error('Erreur lors de la création de la solution');
    } finally {
      setLoading(false);
    }
  };

  const handleSolutionClick = async (solution: Solution) => {
    try {
      // Reload the full solution details
      const fullSolution = await solutionService.getSolution(solution.id);
      setSelectedSolution(fullSolution);
      setEditSolutionModalOpen(true);
    } catch (error) {
      console.error('Error loading solution:', error);
      toast.error('Erreur lors du chargement de la solution');
    }
  };

  const handleCloseSolutionModal = async () => {
    // Save changes before closing
    if (selectedSolution) {
      try {
        await solutionService.updateSolution(selectedSolution.id, {
          latex_template: solutionLatexTemplate,
          python_function: solutionPythonFunction,
          tags: solutionTags,
          skills_with_weights: skillsWithWeights,
        });
        toast.success('Solution mise à jour');

        // Reload solutions list
        if (selectedPattern?.id) {
          const solutions = await solutionService.getSolutionsByPattern(selectedPattern.id);
          setPatternSolutions(solutions);
        }
      } catch (error) {
        console.error('Error updating solution:', error);
        toast.error('Erreur lors de la mise à jour de la solution');
      }
    }

    setEditSolutionModalOpen(false);
    setSelectedSolution(null);
  };

  const handleAddSolutionTag = () => {
    if (solutionTagInput.trim() && !solutionTags.includes(solutionTagInput.trim())) {
      setSolutionTags([...solutionTags, solutionTagInput.trim()]);
      setSolutionTagInput('');
    }
  };

  const handleRemoveSolutionTag = (tagToRemove: string) => {
    setSolutionTags(solutionTags.filter(tag => tag !== tagToRemove));
  };

  const handleSaveSkillsWithWeights = (newSkillsWithWeights: Array<{ skill_id: number; weight: number }>) => {
    setSkillsWithWeights(newSkillsWithWeights);

    // Also update the selectedSkills for display purposes
    const skills = allSkills.filter(skill =>
      newSkillsWithWeights.some(sw => sw.skill_id === skill.id)
    );
    setSelectedSkills(skills);
  };

  const handleVisualizeSolution = async () => {
    if (!selectedSolution) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      // Save current changes before visualizing
      await solutionService.updateSolution(selectedSolution.id, {
        latex_template: solutionLatexTemplate,
        python_function: solutionPythonFunction,
      });

      // Reload PDF
      setSolutionPdfKey(prev => prev + 1);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Erreur lors de la visualisation');
    } finally {
      setLoading(false);
    }
  };

  const handleRegeneratePatternValues = async () => {
    if (!selectedSolution) return;

    try {
      if (!initialLoadDone.current) setLoading(true);
      const response = await solutionService.generatePatternValues(selectedSolution.id);
      if (response.success) {
        const updatedSolution = await solutionService.getSolution(selectedSolution.id);
        setSelectedSolution(updatedSolution);
        toast.success('Valeurs du pattern régénérées');
      } else {
        toast.error(response.error || 'Erreur lors de la génération');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération des valeurs');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateSolutionValues = async () => {
    if (!selectedSolution) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      // First, save the current python_function
      await solutionService.updateSolution(selectedSolution.id, {
        python_function: solutionPythonFunction,
        latex_template: solutionLatexTemplate,
      });

      // Reload the solution to get the saved code
      const reloadedSolution = await solutionService.getSolution(selectedSolution.id);

      // Update local state with saved code
      setSolutionPythonFunction(reloadedSolution.python_function || '');
      setSolutionLatexTemplate(reloadedSolution.latex_template || '');

      // Now generate with the saved code
      const response = await solutionService.generateSolutionValues(selectedSolution.id);
      if (response.success) {
        const updatedSolution = await solutionService.getSolution(selectedSolution.id);
        setSelectedSolution(updatedSolution);
        toast.success('Valeurs de la solution régénérées');
      } else {
        toast.error(response.error || 'Erreur lors de la génération');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération des valeurs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSolutionFromModal = async () => {
    if (!selectedSolution) return;

    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer cette solution pour "${selectedSolution.pattern_title}" ?`
    );

    if (!confirmDelete) return;

    try {
      await solutionService.deleteSolution(selectedSolution.id);
      toast.success('Solution supprimée avec succès');
      setEditSolutionModalOpen(false);
      setSelectedSolution(null);

      // Reload solutions list
      if (selectedPattern?.id) {
        const solutions = await solutionService.getSolutionsByPattern(selectedPattern.id);
        setPatternSolutions(solutions);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression de la solution');
    }
  };

  const handleToggleSolutionPublic = async (newIsPublic: boolean) => {
    if (!selectedSolution) return;

    try {
      await solutionService.updateSolution(selectedSolution.id, { is_public: newIsPublic });
      toast.success(`Solution rendue ${newIsPublic ? 'publique' : 'privée'}`);

      // Update selected solution
      setSelectedSolution({ ...selectedSolution, is_public: newIsPublic });

      // Reload solutions list
      if (selectedPattern?.id) {
        const solutions = await solutionService.getSolutionsByPattern(selectedPattern.id);
        setPatternSolutions(solutions);
      }
    } catch (error) {
      console.error('Error updating solution visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const loadAllImages = async () => {
    try {
      const images = await imageService.getAll({});
      setAllImages(images);
    } catch (error) {
      console.error('Error loading images:', error);
      toast.error('Erreur lors du chargement des images');
    }
  };

  const handleOpenImageSelection = () => {
    loadAllImages();
    setImageSelectionDialogOpen(true);
  };

  const handleAddLibraryImage = async (imageId: number) => {
    if (!selectedPattern) return;

    try {
      const updatedPattern = await patternService.addLibraryImage(selectedPattern.id, imageId);
      setSelectedPattern(updatedPattern);
      await loadItems();
      toast.success('Image ajoutée au pattern');
    } catch (error: any) {
      console.error('Error adding image:', error);
      toast.error('Erreur lors de l\'ajout de l\'image');
    }
  };

  const handleRemoveLibraryImage = async (imageId: number) => {
    if (!selectedPattern) return;

    try {
      const updatedPattern = await patternService.removeLibraryImage(selectedPattern.id, imageId);
      setSelectedPattern(updatedPattern);
      await loadItems();
      toast.success('Image retirée du pattern');
    } catch (error: any) {
      console.error('Error removing image:', error);
      toast.error('Erreur lors du retrait de l\'image');
    }
  };

  const handleCopyImageTag = async (index: number) => {
    const imageTag = `\\includegraphics[width=3in]{&balise_image_${index + 1}}`;
    try {
      await navigator.clipboard.writeText(imageTag);
      toast.success(`Commande LaTeX copiée : ${imageTag}`);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Erreur lors de la copie dans le presse-papier');
    }
  };

  const handleTestGenerations = async () => {
    if (!selectedPattern || !pythonGenerator) return;

    try {
      setGenerationLoading(true);
      const response = await patternService.testGenerator(selectedPattern.id, pythonGenerator);
      if (response.success && response.results) {
        setGenerationResults(response.results);
        // Update the selected pattern with the saved test values
        setSelectedPattern({
          ...selectedPattern,
          last_test_values: response.results
        });
        toast.success('Valeurs générées et sauvegardées');
      } else {
        toast.error(response.error || 'Erreur lors de l\'exécution du générateur');
        setGenerationResults(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'exécution du générateur');
      setGenerationResults(null);
    } finally {
      setGenerationLoading(false);
    }
  };

  const handleFullscreenTest = async () => {
    if (!selectedPattern || !pythonGenerator) return;

    try {
      setFullscreenTestLoading(true);
      const response = await patternService.testGenerator(selectedPattern.id, pythonGenerator);
      if (response.success && response.results) {
        setFullscreenTestResults(response.results);
        toast.success('Valeurs générées avec succès');
      } else {
        toast.error(response.error || 'Erreur lors de l\'exécution du générateur');
        setFullscreenTestResults(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'exécution du générateur');
      setFullscreenTestResults(null);
    } finally {
      setFullscreenTestLoading(false);
    }
  };

  const handleAiPythonTest = async () => {
    if (!selectedPattern || !aiPythonContent) return;

    try {
      setAiPythonTestLoading(true);
      const response = await patternService.testGenerator(selectedPattern.id, aiPythonContent);
      if (response.success && response.results) {
        setAiPythonTestResults(response.results);
        toast.success('Valeurs générées avec succès');
      } else {
        toast.error(response.error || 'Erreur lors de l\'exécution du générateur');
        setAiPythonTestResults(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'exécution du générateur');
      setAiPythonTestResults(null);
    } finally {
      setAiPythonTestLoading(false);
    }
  };

  const handleOpenTreeView = async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);

      // Load all patterns with pagination handling
      const fetchAllPatterns = async (): Promise<ExercisePattern[]> => {
        let allPatterns: ExercisePattern[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await api.get(`/exercise-patterns/?page=${page}&page_size=100`);
          const data = response.data;

          if (Array.isArray(data)) {
            allPatterns = allPatterns.concat(data);
            hasMore = false;
          } else if (data.results) {
            allPatterns = allPatterns.concat(data.results);
            hasMore = !!data.next;
            page++;
          } else {
            hasMore = false;
          }
        }

        return allPatterns;
      };

      const allPatterns = await fetchAllPatterns();
      setAllPatternsForTree(allPatterns);

      // Fetch all containers with a single request
      const allContainers = await patternContainerService.getAllContainers();
      setAllContainersForTree(allContainers);

      setTreeViewOpen(true);
    } catch (error) {
      console.error('Error loading tree data:', error);
      toast.error('Erreur lors du chargement de l\'arborescence');
    } finally {
      setLoading(false);
    }
  };

  const handleSetThumbnail = async (selection: { page: number; x: number; y: number; width: number; height: number }) => {
    if (!selectedPattern) return;

    try {
      if (!initialLoadDone.current) setLoading(true);
      const response = await patternService.setThumbnail(selectedPattern.id, selection);

      if (response.success) {
        toast.success('Miniature définie avec succès');

        // Update the selected pattern with new thumbnail URL
        const updatedPattern = await patternService.getPattern(selectedPattern.id);
        if (updatedPattern) {
          setSelectedPattern(updatedPattern);
        }

        await loadItems();
      } else {
        toast.error(response.error || 'Erreur lors de la définition de la miniature');
      }
    } catch (error: any) {
      console.error('Error setting thumbnail:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la définition de la miniature');
    } finally {
      setLoading(false);
      setThumbnailSelectorOpen(false);
    }
  };

  const handleAiLatexAssist = async () => {
    if (!aiLatexPrompt.trim()) {
      toast.error('Veuillez entrer un prompt');
      return;
    }

    try {
      setAiLatexLoading(true);

      // Construire le contexte complet
      const fullPrompt = `Je travaille sur un template LaTeX pour générer des exercices mathématiques.

Voici le template LaTeX actuel:
\`\`\`latex
${aiLatexContent}
\`\`\`

${aiLatexPrompt}

IMPORTANT : Le template utilise des balises {{variable}} pour les valeurs dynamiques générées par Python.
Ne supprime pas ces balises, elles sont essentielles pour le fonctionnement du système.

Réponds uniquement avec le code LaTeX modifié, sans explications supplémentaires.`;

      const response = await patternService.assistWithAi(fullPrompt);

      if (response.content) {
        // Nettoyer la réponse pour ne garder que le code LaTeX
        let cleanedContent = response.content;

        // Supprimer les balises markdown si présentes
        cleanedContent = cleanedContent.replace(/```latex\n?/g, '').replace(/```\n?/g, '');

        setAiLatexContent(cleanedContent);
        toast.success('Contenu généré avec succès');

        // Regenerate PDF preview
        setAiLatexPdfKey(prev => prev + 1);
      } else {
        toast.error('Aucune réponse de l\'IA');
      }
    } catch (error: any) {
      console.error('Error with AI assist:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setAiLatexLoading(false);
    }
  };

  const handleApplyAiLatexContent = () => {
    setLatexTemplate(aiLatexContent);
    setAiLatexAssistantOpen(false);
    toast.success('Contenu appliqué au template');
  };

  const handleAiPythonAssist = async () => {
    if (!aiPythonPrompt.trim()) {
      toast.error('Veuillez entrer un prompt');
      return;
    }

    try {
      setAiPythonLoading(true);

      // Construire le contexte complet
      const fullPrompt = `Je travaille sur une fonction Python pour générer des valeurs aléatoires pour un exercice mathématique.

Voici le code Python actuel:
\`\`\`python
${aiPythonContent}
\`\`\`

${aiPythonPrompt}

IMPORTANT : Bibliothèques disponibles dans l'environnement :
- random (déjà importé dans tous les générateurs)
- Nombre : classe pour manipuler des nombres (entiers, décimaux, fractions)
  Exemple : Nombre.entier(1, 10) pour un nombre aléatoire entre 1 et 10
  Exemple : Nombre.decimal(1, 10, 2) pour un décimal entre 1 et 10 avec 2 décimales
- Polynome : classe pour générer et manipuler des polynômes
  Exemple : Polynome.generer(degre=2) pour générer un polynôme de degré 2

La fonction doit s'appeler "generate()" ou "generer_valeurs()" et retourner un dictionnaire.
Les clés du dictionnaire correspondent aux balises {{variable}} dans le template LaTeX.

Réponds uniquement avec le code Python modifié, sans explications supplémentaires.`;

      const response = await patternService.assistWithAi(fullPrompt);

      if (response.content) {
        // Nettoyer la réponse pour ne garder que le code Python
        let cleanedContent = response.content;

        // Supprimer les balises markdown si présentes
        cleanedContent = cleanedContent.replace(/```python\n?/g, '').replace(/```\n?/g, '');

        setAiPythonContent(cleanedContent);
        toast.success('Contenu généré avec succès');

        // Regenerate PDF preview
        setAiPythonPdfKey(prev => prev + 1);
      } else {
        toast.error('Aucune réponse de l\'IA');
      }
    } catch (error: any) {
      console.error('Error with AI assist:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setAiPythonLoading(false);
    }
  };

  const handleApplyAiPythonContent = () => {
    setPythonGenerator(aiPythonContent);
    setAiPythonAssistantOpen(false);
    toast.success('Contenu appliqué au générateur');
  };

  const handleRetryWithAiLatex = async (failedLatexContent: string, errorLogs: string) => {
    try {
      // Open AI assistant with the failed content and error logs
      setAiLatexContent(failedLatexContent);
      setAiLatexAssistantOpen(true);

      // Automatically generate a fix prompt based on the error logs
      const fixPrompt = `Le code LaTeX suivant a échoué à compiler. Voici les logs d'erreur de compilation :

\`\`\`
${errorLogs}
\`\`\`

Analyse les erreurs et corrige le code LaTeX pour qu'il compile correctement. N'oublie pas de conserver les balises {{variable}} pour les valeurs dynamiques. Réponds uniquement avec le code LaTeX corrigé, sans explications.`;

      setAiLatexPrompt(fixPrompt);

      // Automatically trigger AI generation
      setAiLatexLoading(true);

      const fullPrompt = `Je travaille sur un template LaTeX pour générer des exercices mathématiques.

Voici le template LaTeX actuel:
\`\`\`latex
${failedLatexContent}
\`\`\`

${fixPrompt}

IMPORTANT : Le template utilise des balises {{variable}} pour les valeurs dynamiques générées par Python.
Ne supprime pas ces balises, elles sont essentielles pour le fonctionnement du système.

Réponds uniquement avec le code LaTeX modifié, sans explications supplémentaires.`;

      const response = await patternService.assistWithAi(fullPrompt);

      if (response.content) {
        let cleanedContent = response.content;
        cleanedContent = cleanedContent.replace(/```latex\n?/g, '').replace(/```\n?/g, '');

        setAiLatexContent(cleanedContent);
        toast.success('Code LaTeX corrigé généré avec succès');

        // Regenerate PDF preview
        setAiLatexPdfKey(prev => prev + 1);
      } else {
        toast.error('Aucune réponse de l\'IA');
      }
    } catch (error: any) {
      console.error('Error with AI fix:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la correction automatique');
    } finally {
      setAiLatexLoading(false);
    }
  };

  const handleRetryWithAiPython = async (failedPythonContent: string, errorLogs: string) => {
    try {
      // Open AI assistant with the failed content and error logs
      setAiPythonContent(failedPythonContent);
      setAiPythonAssistantOpen(true);

      // Automatically generate a fix prompt based on the error logs
      const fixPrompt = `Le code Python suivant a généré une erreur. Voici les logs d'erreur :

\`\`\`
${errorLogs}
\`\`\`

Analyse les erreurs et corrige le code Python. Réponds uniquement avec le code Python corrigé, sans explications.`;

      setAiPythonPrompt(fixPrompt);

      // Automatically trigger AI generation
      setAiPythonLoading(true);

      const fullPrompt = `Je travaille sur une fonction Python pour générer des valeurs aléatoires pour un exercice mathématique.

Voici le code Python actuel:
\`\`\`python
${failedPythonContent}
\`\`\`

${fixPrompt}

IMPORTANT : Bibliothèques disponibles dans l'environnement :
- random (déjà importé dans tous les générateurs)
- Nombre : classe pour manipuler des nombres (entiers, décimaux, fractions)
- Polynome : classe pour générer et manipuler des polynômes

La fonction doit s'appeler "generate()" ou "generer_valeurs()" et retourner un dictionnaire.
Les clés du dictionnaire correspondent aux balises {{variable}} dans le template LaTeX.

Réponds uniquement avec le code Python modifié, sans explications supplémentaires.`;

      const response = await patternService.assistWithAi(fullPrompt);

      if (response.content) {
        let cleanedContent = response.content;
        cleanedContent = cleanedContent.replace(/```python\n?/g, '').replace(/```\n?/g, '');

        setAiPythonContent(cleanedContent);
        toast.success('Code Python corrigé généré avec succès');

        // Regenerate PDF preview
        setAiPythonPdfKey(prev => prev + 1);
      } else {
        toast.error('Aucune réponse de l\'IA');
      }
    } catch (error: any) {
      console.error('Error with AI fix:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la correction automatique');
    } finally {
      setAiPythonLoading(false);
    }
  };

  const handleOpenCompositeDialog = (cp?: any) => {
    if (cp) {
      // Edit mode: load full composite pattern
      setEditingCompositeId(cp.id);
      compositePatternService.get(cp.id).then(full => {
        setCompositeTitle(full.title);
        setCompositeChildPatterns(
          (full.items || []).map((item: any) => ({
            id: item.exercise_pattern,
            title: item.pattern_title,
          } as any))
        );
        setCreateType('composite');
        setPatternDialogOpen(true);
      });
    } else {
      setEditingCompositeId(null);
      setCompositeTitle('');
      setCompositeChildPatterns([]);
      setCreateType('composite');
      setPatternDialogOpen(true);
    }
  };

  const handleOpenCompositeTree = async () => {
    try {
      const fetchAllPatterns = async (): Promise<ExercisePattern[]> => {
        let all: ExercisePattern[] = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const response = await api.get(`/exercise-patterns/?page=${page}&page_size=100`);
          const data = response.data;
          if (Array.isArray(data)) { all = all.concat(data); hasMore = false; }
          else if (data.results) { all = all.concat(data.results); hasMore = !!data.next; page++; }
          else { hasMore = false; }
        }
        return all;
      };
      const [allP, allC] = await Promise.all([
        fetchAllPatterns(),
        patternContainerService.getAllContainers(),
      ]);
      setCompositeTreePatterns(allP);
      setCompositeTreeContainers(allC);
      setCompositeTreeOpen(true);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'arborescence");
    }
  };

  const handleCompositeSearch = async (query: string) => {
    if (!query || query.length < 2) {
      setCompositeSearchResults([]);
      return;
    }
    try {
      setCompositeSearchLoading(true);
      const response = await api.get(`/exercise-patterns/?search=${encodeURIComponent(query)}&all=true`);
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setCompositeSearchResults(data);
    } catch {
      setCompositeSearchResults([]);
    } finally {
      setCompositeSearchLoading(false);
    }
  };

  const handleCompositeTreeSelect = (pattern: ExercisePattern) => {
    // If editing in the right panel, add directly to backend
    if (selectedCompositePattern && !patternDialogOpen) {
      if (!selectedCompositePattern.items?.find(it => it.exercise_pattern === pattern.id)) {
        const currentIds = (selectedCompositePattern.items || []).map(it => it.exercise_pattern);
        compositePatternService.update(selectedCompositePattern.id, {
          child_pattern_ids: [...currentIds, pattern.id],
        }).then(updated => {
          setSelectedCompositePattern(updated);
          loadItems();
          toast.success('Pattern ajouté');
        }).catch(() => toast.error('Erreur'));
      }
    } else {
      // Creation dialog mode
      if (!compositeChildPatterns.find(p => p.id === pattern.id)) {
        setCompositeChildPatterns(prev => [...prev, pattern]);
      }
    }
    setCompositeTreeOpen(false);
  };

  const handleSaveComposite = async () => {
    if (!compositeTitle.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    try {
      const data = {
        title: compositeTitle,
        child_pattern_ids: compositeChildPatterns.map(p => p.id),
        container: currentContainer?.id || null,
      };
      if (editingCompositeId) {
        await compositePatternService.update(editingCompositeId, data);
        toast.success('Pattern composé mis à jour');
      } else {
        await compositePatternService.create(data);
        toast.success('Pattern composé créé');
      }
      setPatternDialogOpen(false);
      setCompositeTitle('');
      setCompositeChildPatterns([]);
      setEditingCompositeId(null);
      setCreateType('pattern');
      loadItems();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Pattern List */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {currentContainer ? currentContainer.title : 'Patterns'}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={handleNewPattern}
              >
                Nouveau
              </Button>
            </Box>

            <Box mb={2}>
              <Button
                startIcon={<TreeIcon />}
                size="small"
                onClick={handleOpenTreeView}
                fullWidth
                variant="outlined"
              >
                Arborescence complète
              </Button>
            </Box>

            <Box display="flex" alignItems="center" mb={1}>
              <Switch size="small" checked={showDescriptions} onChange={(e) => setShowDescriptions(e.target.checked)} />
              <Typography variant="caption" color="text.secondary">Descriptions</Typography>
            </Box>

            {loading ? (
              <CircularProgress />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={[
                    ...(currentContainer ? ['parent-folder'] : []),
                    ...(Array.isArray(containers) ? containers.map(c => `container-${c.id}`) : []),
                    ...(Array.isArray(compositePatterns) ? compositePatterns.map(cp => `composite-${cp.id}`) : []),
                    ...(Array.isArray(patterns) ? patterns.map(p => `pattern-${p.id}`) : [])
                  ]}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1}>
                    {currentContainer && (
                      <ParentDropZone
                        parentContainer={navigationPath[navigationPath.length - 2] || null}
                        onDrop={handleDropToParent}
                        onClick={handleNavigateBack}
                      />
                    )}
                    {Array.isArray(containers) && containers.map((container) => (
                      <SortableItem
                        key={`container-${container.id}`}
                        item={container}
                        isContainer={true}
                        isSelected={false}
                        onSelect={handlePatternSelect}
                        onDeleteContainer={handleDeleteContainer}
                        onRenameItem={handleRenameItem}
                        activeItemType={activeItemType}
                        showDescription={showDescriptions}
                      />
                    ))}
                    {Array.isArray(compositePatterns) && compositePatterns.map((cp) => (
                      <SortableItem
                        key={`composite-${cp.id}`}
                        item={cp}
                        itemKind="composite"
                        isSelected={selectedCompositePattern?.id === cp.id}
                        onSelect={handlePatternSelect}
                        onDeleteComposite={handleDeleteComposite}
                        onRenameItem={handleRenameItem}
                        activeItemType={activeItemType}
                      />
                    ))}
                    {Array.isArray(patterns) && patterns.map((pattern) => (
                      <SortableItem
                        key={`pattern-${pattern.id}`}
                        item={pattern}
                        isContainer={false}
                        isSelected={selectedPattern?.id === pattern.id}
                        onSelect={handlePatternSelect}
                        onDeletePattern={handleDeletePattern}
                        onRenameItem={handleRenameItem}
                        onQuickRecapClick={handleQuickRecapClick}
                        onTogglePublic={handleTogglePublic}
                        activeItemType={activeItemType}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Grid>

        {/* Right Panel - Editor */}
        <Grid item xs={12} md={9}>
          <Paper sx={{
            p: 3,
            backgroundColor: selectedPattern?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
            borderRadius: 2
          }}>
            {selectedCompositePattern ? (
              /* Composite Pattern Editor */
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TreeIcon sx={{ color: 'secondary.main' }} />
                    {selectedCompositePattern.title}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => setSelectedCompositePattern(null)}
                  >
                    Fermer
                  </Button>
                </Box>

                <Typography variant="subtitle2" mb={1}>
                  Patterns composants ({selectedCompositePattern.items?.length || 0})
                </Typography>

                <Stack spacing={1} mb={2}>
                  {(selectedCompositePattern.items || []).map((item, idx) => (
                    <Card key={item.id} variant="outlined" sx={{ borderColor: 'secondary.light' }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileIcon sx={{ color: 'action.disabled', fontSize: 18 }} />
                        <Typography variant="body2" flex={1}>{item.pattern_title}</Typography>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            const newItems = (selectedCompositePattern.items || []).filter((_, i) => i !== idx);
                            const newIds = newItems.map(it => it.exercise_pattern);
                            try {
                              const updated = await compositePatternService.update(selectedCompositePattern.id, {
                                child_pattern_ids: newIds,
                              });
                              setSelectedCompositePattern(updated);
                              loadItems();
                              toast.success('Pattern retiré');
                            } catch {
                              toast.error('Erreur');
                            }
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                <Typography variant="subtitle2" mb={1}>Ajouter un pattern</Typography>
                <PatternBrowser
                  onSelect={(pattern) => {
                    if (!selectedCompositePattern.items?.find(it => it.exercise_pattern === pattern.id)) {
                      const currentIds = (selectedCompositePattern.items || []).map(it => it.exercise_pattern);
                      compositePatternService.update(selectedCompositePattern.id, {
                        child_pattern_ids: [...currentIds, pattern.id],
                      }).then(updated => {
                        setSelectedCompositePattern(updated);
                        loadItems();
                        toast.success('Pattern ajouté');
                      }).catch(() => toast.error('Erreur'));
                    }
                  }}
                  excludeIds={(selectedCompositePattern.items || []).map(it => it.exercise_pattern)}
                  maxHeight={300}
                />
              </Box>
            ) : (
            <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Éditeur de Pattern
              </Typography>
            </Box>

            {!selectedPattern ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="400px"
                color="text.secondary"
              >
                {loadingPatternId !== null ? (
                  <CircularProgress />
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Aucun pattern sélectionné
                    </Typography>
                    <Typography variant="body2">
                      Sélectionnez un pattern dans la liste ou créez-en un nouveau
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
            <Grid container spacing={2}>
              {/* Template LaTeX à gauche */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Template LaTeX</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<FullscreenIcon />}
                      onClick={() => setFullscreenLatexEditorOpen(true)}
                      variant="outlined"
                      disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                    >
                      Plein écran
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AiIcon />}
                      onClick={() => {
                        setAiLatexContent(latexTemplate);
                        setAiLatexPrompt('');
                        setAiLatexAssistantOpen(true);
                      }}
                      variant="contained"
                      color="secondary"
                      disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                    >
                      Assistant IA
                    </Button>
                  </Stack>
                </Box>
                <LatexEditor
                  value={latexTemplate}
                  onChange={(newValue) => {
                    // Allow editing if user is author
                    if (selectedPattern && (!selectedPattern.is_public || isUserAuthor(selectedPattern))) {
                      setLatexTemplate(newValue);
                    }
                  }}
                  helperText="Utilisez {{variable}} pour les valeurs dynamiques"
                  height={600}
                  disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                />
              </Grid>

              {/* Aperçu PDF à droite */}
              <Grid item xs={12} md={6}>
                <Box sx={{ height: '600px' }}>
                  <Box display="flex" justifyContent="flex-end" mb={1}>
                    <Button
                      variant="contained"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreview}
                      disabled={!selectedPattern}
                    >
                      Visualiser les itérations
                    </Button>
                  </Box>
                  {showPdfPreview && selectedPattern ? (
                    <Paper variant="outlined" sx={{ p: 1, height: 'calc(100% - 36px)', overflow: 'auto' }}>
                      <PatternPdfViewer
                        key={pdfPreviewKey}
                        latexTemplate={latexTemplate}
                        pythonGenerator={pythonGenerator}
                        isReusable={isReusable}
                        iterations={isReusable ? 3 : 1}
                        patternId={selectedPattern?.id}
                        onRetryWithAiLatex={handleRetryWithAiLatex}
                        onRetryWithAiPython={handleRetryWithAiPython}
                        onPdfGenerated={async () => {
                          // PDF generated successfully - reload pattern to get preview_pdf_url
                          // PDF generated successfully - reload pattern to get preview_pdf_url
                          if (selectedPattern?.id) {
                            try {
                              const updatedPattern = await patternService.getPattern(selectedPattern.id);
                              if (updatedPattern) {
                                setSelectedPattern(updatedPattern);
                              }
                            } catch (error) {
                              console.error('Error reloading pattern after PDF generation:', error);
                            }
                          }
                        }}
                      />
                    </Paper>
                  ) : selectedPattern?.preview_pdf_url ? (
                    <Paper variant="outlined" sx={{ p: 1, height: 'calc(100% - 36px)', overflow: 'auto' }}>
                      <PdfDisplay
                        url={selectedPattern.preview_pdf_url}
                        title="Aperçu du pattern"
                      />
                    </Paper>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        height: 'calc(100% - 36px)',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 3
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        Cliquez sur "Visualiser les itérations" pour générer un aperçu PDF
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Fonction Python à gauche */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Fonction Python de génération</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<FullscreenIcon />}
                      onClick={() => {
                        setFullscreenPythonEditorOpen(true);
                        // Reset test results when opening
                        setFullscreenTestResults(null);
                      }}
                      variant="outlined"
                      color="primary"
                      disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern)) || !isReusable}
                    >
                      Plein écran
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AiIcon />}
                      onClick={() => {
                        setAiPythonContent(pythonGenerator);
                        setAiPythonPrompt('');
                        setAiPythonAssistantOpen(true);
                        // Reset test results when opening
                        setAiPythonTestResults(null);
                      }}
                      variant="contained"
                      color="secondary"
                      disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern)) || !isReusable}
                    >
                      Assistant IA
                    </Button>
                  </Stack>
                </Box>
                <PythonEditor
                  value={pythonGenerator}
                  onChange={(value) => {
                    // Allow editing if user is author
                    if (selectedPattern && (!selectedPattern.is_public || isUserAuthor(selectedPattern))) {
                      setPythonGenerator(value);
                    }
                  }}
                  helperText="Définissez une fonction generate() qui retourne un dictionnaire"
                  disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern)) || !isReusable}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '400px',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important',
                      maxHeight: '100%',
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      WebkitTextFillColor: '#666',
                    },
                    '& textarea': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    }
                  }}
                />
              </Grid>

              {/* Résultats de génération à droite */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Tooltip
                      title={isReusable ? "On peut générer plusieurs exercices à partir de ce pattern" : "Ce n'est pas un exercice réitérable"}
                      placement="top"
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isReusable}
                            onChange={(e) => {
                              // Allow editing if user is author
                              if (selectedPattern && (!selectedPattern.is_public || isUserAuthor(selectedPattern))) {
                                setIsReusable(e.target.checked);
                              }
                            }}
                            disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                          />
                        }
                        label={isReusable ? "Réutilisable" : "Statique"}
                      />
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleTestGenerations}
                      disabled={!selectedPattern || !isReusable || (selectedPattern.is_public && !isUserAuthor(selectedPattern)) || generationLoading}
                    >
                      {generationLoading ? 'Exécution...' : 'Générer'}
                    </Button>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      height: '400px',
                      overflow: 'auto',
                      backgroundColor: '#f5f5f5',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {generationLoading ? (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <CircularProgress size={24} />
                      </Box>
                    ) : !selectedPattern || !isReusable ? (
                      <Typography variant="body2" color="text.secondary">
                        Sélectionnez un pattern réutilisable pour voir les générations
                      </Typography>
                    ) : generationResults ? (
                      <Box>
                        {generationResults.map((result, index) => (
                          <Box key={index} mb={2}>
                            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                              Génération {index + 1}:
                            </Typography>
                            <Box
                              sx={{
                                backgroundColor: '#fff',
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid #ddd'
                              }}
                            >
                              {JSON.stringify(result, null, 2)}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Cliquez sur "Générer" pour exécuter la fonction
                      </Typography>
                    )}
                  </Paper>
                </Box>
              </Grid>

              {/* Images section */}
              <Grid item xs={12}>
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Images du pattern</Typography>
                    <Button
                      size="small"
                      startIcon={<ImageIcon />}
                      onClick={handleOpenImageSelection}
                      disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                    >
                      Gérer les images
                    </Button>
                  </Box>
                  {selectedPattern?.library_images_details && selectedPattern.library_images_details.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        py: 1,
                        '&::-webkit-scrollbar': {
                          height: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '4px',
                        }
                      }}
                    >
                      {selectedPattern.library_images_details.map((img, index) => (
                        <Box
                          key={img.id}
                          onClick={() => handleCopyImageTag(index)}
                          sx={{
                            position: 'relative',
                            minWidth: '120px',
                            minHeight: '100px',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: 2,
                            },
                            '&:hover .delete-btn': {
                              opacity: 1,
                            }
                          }}
                        >
                          {img.thumbnail_url || img.image_file_url ? (
                            <Box
                              component="img"
                              src={img.thumbnail_url || img.image_file_url || ''}
                              alt={img.title}
                              sx={{
                                width: '120px',
                                height: '100px',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '120px',
                                height: '100px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'action.hover',
                              }}
                            >
                              <ImageIcon />
                            </Box>
                          )}
                          {!selectedPattern.is_public && (
                            <IconButton
                              className="delete-btn"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveLibraryImage(img.id);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'background.paper',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '&:hover': {
                                  bgcolor: 'error.main',
                                  color: 'error.contrastText',
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              p: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold',
                              }}
                            >
                              &balise_image_{index + 1}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.65rem',
                              }}
                            >
                              {img.title}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 1,
                        p: 3,
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Aucune image ajoutée à ce pattern
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Thumbnail and Tags section - side by side */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* Thumbnail section */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        borderColor: 'divider',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <ImageIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                          Miniature du pattern
                        </Typography>
                      </Box>
                      {selectedPattern?.thumbnail_url ? (
                        <Box>
                          <Box
                            component="img"
                            src={selectedPattern.thumbnail_url}
                            alt="Miniature du pattern"
                            sx={{
                              width: '100%',
                              maxHeight: '180px',
                              objectFit: 'contain',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'grey.50',
                              display: 'block',
                              mb: 1.5
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setThumbnailSelectorOpen(true)}
                            disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern)) || !selectedPattern.preview_pdf_url}
                          >
                            Modifier
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 3,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            textAlign: 'center',
                            bgcolor: 'grey.50',
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Aucune miniature définie
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setThumbnailSelectorOpen(true)}
                            disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern)) || !selectedPattern.preview_pdf_url}
                            sx={{ mt: 1 }}
                          >
                            Définir une miniature
                          </Button>
                          {(!selectedPattern?.preview_pdf_url) && (
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                              Générez d'abord un aperçu PDF
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Tags section */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        borderColor: 'divider',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                          }}
                        >
                          🏷️
                        </Box>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                          Tags
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          minHeight: '100px',
                          p: 2,
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                          mb: 1.5,
                        }}
                      >
                        {tags.length > 0 ? (
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {tags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                onDelete={(selectedPattern?.is_public && !isUserAuthor(selectedPattern)) ? undefined : () => handleRemoveTag(tag)}
                                size="small"
                                disabled={selectedPattern?.is_public && !isUserAuthor(selectedPattern)}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center' }}>
                            Aucun tag ajouté
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" gap={1}>
                        <TextField
                          size="small"
                          placeholder="Ajouter un tag"
                          value={tagInput}
                          onChange={(e) => {
                            if (selectedPattern && (!selectedPattern.is_public || isUserAuthor(selectedPattern))) {
                              setTagInput(e.target.value);
                            }
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                          disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                          InputProps={{
                            readOnly: selectedPattern?.is_public && !isUserAuthor(selectedPattern)
                          }}
                          sx={{
                            flex: 1,
                            '& .MuiInputBase-input.Mui-disabled': {
                              backgroundColor: '#f5f5f5',
                              WebkitTextFillColor: '#666',
                            }
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddTag}
                          disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                        >
                          Ajouter
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              {/* Solutions section */}
              {selectedPattern && (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      borderColor: 'divider',
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                          }}
                        >
                          📝
                        </Box>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                          Solutions utilisant ce pattern ({patternSolutions.length})
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleCreateSolutionClick}
                        disabled={!selectedPattern || loading}
                      >
                        Créer une solution
                      </Button>
                    </Box>

                    {solutionsLoading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : patternSolutions.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          overflowX: 'auto',
                          pb: 2,
                          '&::-webkit-scrollbar': {
                            height: 8,
                          },
                          '&::-webkit-scrollbar-track': {
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'grey.400',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'grey.500',
                            },
                          },
                        }}
                      >
                        {patternSolutions.map((solution) => (
                          <Box key={solution.id} sx={{ flexShrink: 0 }}>
                            <SolutionCard solution={solution} onClick={handleSolutionClick} />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Aucune solution n'utilise ce pattern d'exercice pour le moment
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Redaction elements section (sent to AI grader) */}
              {selectedPattern && (
                <Grid item xs={12}>
                  <PatternRedactionElementsSection
                    pattern={selectedPattern}
                    readOnly={selectedPattern.is_public && !isUserAuthor(selectedPattern)}
                    onPatternFlagsChange={async (flags) => {
                      try {
                        const updated = await patternService.updatePattern(selectedPattern.id, flags);
                        setSelectedPattern({ ...selectedPattern, ...updated });
                      } catch {
                        toast.error('Mise à jour impossible');
                      }
                    }}
                  />
                </Grid>
              )}

              {selectedPattern?.is_public && !isUserAuthor(selectedPattern) && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DuplicateIcon />}
                    onClick={handleDuplicatePattern}
                    disabled={!selectedPattern || loading}
                  >
                    Dupliquer
                  </Button>
                </Grid>
              )}
            </Grid>
            )}
            </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Pattern/Container Creation Dialog */}
      {patternDialogOpen && (
        <Dialog open={patternDialogOpen} onClose={() => { setPatternDialogOpen(false); setContainerDescription(''); }}>
          <DialogTitle>Nouveau</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                value={createType}
                exclusive
                onChange={(e, newValue) => newValue && setCreateType(newValue)}
                fullWidth
                size="small"
              >
                <ToggleButton value="container">
                  <FolderIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Contenant
                </ToggleButton>
                <ToggleButton value="pattern">
                  <FileIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Pattern
                </ToggleButton>
                <ToggleButton value="composite">
                  <TreeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Composé
                </ToggleButton>
                <ToggleButton value="existing">
                  <DuplicateIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Dupliquer
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {createType === 'container' && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Nom du contenant"
                  fullWidth
                  variant="outlined"
                  value={containerTitle}
                  onChange={(e) => setContainerTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateContainer();
                    }
                  }}
                />
                <TextField
                  margin="dense"
                  label="Description (optionnel)"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  value={containerDescription}
                  onChange={(e) => setContainerDescription(e.target.value)}
                />
              </>
            )}
            {createType === 'pattern' && (
              <TextField
                autoFocus
                margin="dense"
                label="Nom du pattern"
                fullWidth
                variant="outlined"
                value={newPatternTitle}
                onChange={(e) => setNewPatternTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newPatternTitle.trim()) {
                    handleCreateNewPattern();
                  }
                }}
              />
            )}
            {createType === 'composite' && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Titre du pattern composé"
                  fullWidth
                  variant="outlined"
                  value={compositeTitle}
                  onChange={(e) => setCompositeTitle(e.target.value)}
                />
                <Box sx={{ mt: 2 }}>
                  <PatternBrowser
                    onSelect={(pattern) => {
                      if (!compositeChildPatterns.find(p => p.id === pattern.id)) {
                        setCompositeChildPatterns(prev => [...prev, pattern]);
                      }
                    }}
                    excludeIds={compositeChildPatterns.map(p => p.id)}
                    maxHeight={250}
                  />
                </Box>
                {compositeChildPatterns.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
                    {compositeChildPatterns.map((p, idx) => (
                      <Chip
                        key={p.id}
                        label={`${idx + 1}. ${p.title}`}
                        onDelete={() => setCompositeChildPatterns(prev => prev.filter(x => x.id !== p.id))}
                        variant="outlined"
                        color="secondary"
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
            {createType === 'existing' && (
              <Box sx={{ mt: 2, minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
                <PatternChoiceContent
                  onSelect={(pattern) => {
                    handlePatternChoiceSelect(pattern);
                    setPatternDialogOpen(false);
                    setCreateType('pattern');
                  }}
                  currentContainerId={currentContainer?.id}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setPatternDialogOpen(false);
              setContainerTitle('');
              setContainerDescription('');
              setNewPatternTitle('');
              setCompositeTitle('');
              setCompositeChildPatterns([]);
              setCreateType('pattern');
            }}>
              Annuler
            </Button>
            {createType === 'container' && (
              <Button onClick={handleCreateContainer} variant="contained" color="primary">
                Créer le contenant
              </Button>
            )}
            {createType === 'pattern' && (
              <Button
                onClick={handleCreateNewPattern}
                variant="contained"
                color="primary"
                disabled={!newPatternTitle.trim()}
              >
                Créer le pattern
              </Button>
            )}
            {createType === 'composite' && (
              <Button
                onClick={handleSaveComposite}
                variant="contained"
                color="secondary"
                disabled={!compositeTitle.trim()}
              >
                {editingCompositeId ? 'Modifier' : 'Créer le composé'}
              </Button>
            )}
            {createType === 'existing' && (
              <Button onClick={() => {
                setPatternDialogOpen(false);
                setCreateType('pattern');
              }}>
                Fermer
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Supprimer le pattern"
        itemName={patternToDelete?.title || ''}
        isPublic={patternToDelete?.is_public || false}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setPatternToDelete(null);
        }}
      />

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => { setRenameDialogOpen(false); setEditContainerDescription(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Renommer {itemToRename?.isContainer ? 'le contenant' : 'le pattern'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nouveau titre"
            fullWidth
            variant="outlined"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          {itemToRename?.isContainer && (
            <>
              <TextField
                margin="dense"
                label="Description (optionnel)"
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                value={editContainerDescription}
                onChange={(e) => setEditContainerDescription(e.target.value)}
              />
              <ThumbnailPicker
                value={editContainerThumbnail}
                onChange={setEditContainerThumbnail}
                currentThumbnailUrl={(itemToRename?.item as any)?.thumbnail_url}
              />
            </>
          )}
          {itemToRename && !itemToRename.isContainer && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" display="block" color="textSecondary">
                Date de création: {new Date((itemToRename.item as ExercisePattern).created_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
              <Typography variant="caption" display="block" color="textSecondary">
                Dernière modification: {new Date((itemToRename.item as ExercisePattern).updated_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRenameDialogOpen(false); setEditContainerDescription(''); }}>Annuler</Button>
          <Button
            onClick={handleConfirmRename}
            variant="contained"
            disabled={!renameTitle.trim()}
          >
            Renommer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Solution Dialog */}
      <Dialog
        open={createSolutionDialogOpen}
        onClose={() => setCreateSolutionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Créer une nouvelle solution</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Container Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Container de destination
              </Typography>
              <Autocomplete
                options={solutionContainers}
                getOptionLabel={(option) => option.title}
                value={selectedSolutionContainer}
                onChange={(_, newValue) => setSelectedSolutionContainer(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Sélectionnez un container..."
                    variant="outlined"
                    size="small"
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSolutionDialogOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleCreateSolution}
            variant="contained"
            disabled={loading || !selectedSolutionContainer}
            startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Selection Dialog */}
      <Dialog
        open={imageSelectionDialogOpen}
        onClose={() => setImageSelectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Sélectionner des images</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {allImages.map((image) => {
              const isAdded = selectedPattern?.library_images?.includes(image.id);
              return (
                <Grid item xs={6} sm={4} md={3} key={image.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: isAdded ? '2px solid' : '1px solid',
                      borderColor: isAdded ? 'primary.main' : 'divider',
                      position: 'relative',
                    }}
                    onClick={() => {
                      if (isAdded) {
                        handleRemoveLibraryImage(image.id);
                      } else {
                        handleAddLibraryImage(image.id);
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 120,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                      }}
                    >
                      {image.thumbnail_url || image.image_file_url ? (
                        <Box
                          component="img"
                          src={image.thumbnail_url || image.image_file_url || ''}
                          alt={image.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <ImageIcon sx={{ fontSize: 40 }} />
                      )}
                    </Box>
                    <CardContent sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {image.title}
                      </Typography>
                    </CardContent>
                    {isAdded && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 16,
                        }}
                      >
                        ✓
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {allImages.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              Aucune image disponible
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageSelectionDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Thumbnail Selector */}
      {selectedPattern?.preview_pdf_url && (
        <ThumbnailSelector
          open={thumbnailSelectorOpen}
          onClose={() => setThumbnailSelectorOpen(false)}
          pdfUrl={selectedPattern.preview_pdf_url}
          onConfirm={handleSetThumbnail}
        />
      )}

      {/* AI Assistant Dialog for LaTeX */}
      <Dialog
        open={aiLatexAssistantOpen}
        onClose={() => setAiLatexAssistantOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AiIcon />
            <Typography variant="h6">Assistant IA - Template LaTeX</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Column 1: Prompt Input */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                Prompt pour l'IA
              </Typography>
              <TextField
                fullWidth
                multiline
                value={aiLatexPrompt}
                onChange={(e) => setAiLatexPrompt(e.target.value)}
                placeholder="Décrivez les modifications que vous souhaitez apporter au template LaTeX..."
                variant="outlined"
                sx={{
                  flex: 1,
                  mb: 2,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                  },
                  '& textarea': {
                    height: '100% !important',
                    overflow: 'auto !important',
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleAiLatexAssist}
                disabled={aiLatexLoading || !aiLatexPrompt.trim()}
                startIcon={aiLatexLoading ? <CircularProgress size={20} /> : <AiIcon />}
              >
                {aiLatexLoading ? 'Génération...' : 'Générer avec IA'}
              </Button>
            </Grid>

            {/* Column 2: LaTeX Content Display */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                Template LaTeX
              </Typography>
              <TextField
                fullWidth
                multiline
                value={aiLatexContent}
                onChange={(e) => setAiLatexContent(e.target.value)}
                variant="outlined"
                sx={{
                  flex: 1,
                  fontFamily: 'monospace',
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                  '& textarea': {
                    height: '100% !important',
                    overflow: 'auto !important',
                  }
                }}
              />
            </Grid>

            {/* Column 3: PDF Preview */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                Prévisualisation PDF
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {aiLatexContent && pythonGenerator ? (
                  <PatternPdfViewer
                    key={aiLatexPdfKey}
                    latexTemplate={aiLatexContent}
                    pythonGenerator={pythonGenerator}
                    isReusable={isReusable}
                    iterations={isReusable ? 3 : 1}
                    onRetryWithAiLatex={handleRetryWithAiLatex}
                    onRetryWithAiPython={handleRetryWithAiPython}
                  />
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {!aiLatexContent ? 'Aucun contenu LaTeX à prévisualiser' : 'Générateur Python manquant'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiLatexAssistantOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleApplyAiLatexContent} variant="contained" color="primary">
            Appliquer au template
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Assistant Dialog for Python */}
      <Dialog
        open={aiPythonAssistantOpen}
        onClose={() => setAiPythonAssistantOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AiIcon />
            <Typography variant="h6">Assistant IA - Générateur Python</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Column 1: Prompt Input */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                Prompt pour l'IA
              </Typography>
              <TextField
                fullWidth
                multiline
                value={aiPythonPrompt}
                onChange={(e) => setAiPythonPrompt(e.target.value)}
                placeholder="Décrivez le type de générateur Python que vous souhaitez créer..."
                variant="outlined"
                sx={{
                  flex: 1,
                  mb: 2,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                  },
                  '& textarea': {
                    height: '100% !important',
                    overflow: 'auto !important',
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAiPythonAssist}
                disabled={aiPythonLoading || !aiPythonPrompt.trim()}
                startIcon={aiPythonLoading ? <CircularProgress size={20} /> : <AiIcon />}
              >
                {aiPythonLoading ? 'Génération...' : 'Générer avec IA'}
              </Button>
            </Grid>

            {/* Column 2: Python Content Display */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                Code Python
              </Typography>
              <TextField
                fullWidth
                multiline
                value={aiPythonContent}
                onChange={(e) => setAiPythonContent(e.target.value)}
                variant="outlined"
                sx={{
                  flex: 1,
                  fontFamily: 'monospace',
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                  '& textarea': {
                    height: '100% !important',
                    overflow: 'auto !important',
                  }
                }}
              />
            </Grid>

            {/* Column 3: Test Results */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Résultats de génération
                </Typography>
                <Button
                  size="small"
                  onClick={handleAiPythonTest}
                  variant="contained"
                  disabled={!aiPythonContent || aiPythonTestLoading}
                >
                  {aiPythonTestLoading ? <CircularProgress size={20} /> : 'Générer'}
                </Button>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  flexGrow: 1,
                  p: 2,
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5'
                }}
              >
                {aiPythonTestLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <CircularProgress size={24} />
                  </Box>
                ) : aiPythonTestResults ? (
                  <Box>
                    {aiPythonTestResults.map((result, index) => (
                      <Box key={index} mb={2}>
                        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                          Génération {index + 1}:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#fff',
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {JSON.stringify(result, null, 2)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Cliquez sur "Générer" pour tester le code
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiPythonAssistantOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleApplyAiPythonContent} variant="contained" color="primary">
            Appliquer au générateur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Python Editor Dialog */}
      <Dialog
        open={fullscreenPythonEditorOpen}
        onClose={() => setFullscreenPythonEditorOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FullscreenIcon />
            <Typography variant="h6">Éditeur Python - Plein Écran</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Column 1 & 2: Python Editor (占 2/3 width) */}
            <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                Code Python
              </Typography>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <PythonEditor
                  value={pythonGenerator}
                  onChange={(value) => {
                    if (selectedPattern && (!selectedPattern.is_public || isUserAuthor(selectedPattern))) {
                      setPythonGenerator(value);
                    }
                  }}
                  helperText="Définissez une fonction generate() qui retourne un dictionnaire"
                  disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern)) || !isReusable}
                  sx={{
                    flexGrow: 1,
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'stretch',
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Column 3: Test Results */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Résultats de génération
                </Typography>
                <Button
                  size="small"
                  onClick={handleFullscreenTest}
                  variant="contained"
                  disabled={!pythonGenerator || fullscreenTestLoading}
                >
                  {fullscreenTestLoading ? <CircularProgress size={20} /> : 'Générer'}
                </Button>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  flexGrow: 1,
                  p: 2,
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5'
                }}
              >
                {fullscreenTestLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <CircularProgress size={24} />
                  </Box>
                ) : fullscreenTestResults ? (
                  <Box>
                    {fullscreenTestResults.map((result, index) => (
                      <Box key={index} mb={2}>
                        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                          Génération {index + 1}:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#fff',
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {JSON.stringify(result, null, 2)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Cliquez sur "Générer" pour tester la fonction
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullscreenPythonEditorOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen LaTeX Editor Dialog */}
      <Dialog
        open={fullscreenLatexEditorOpen}
        onClose={() => setFullscreenLatexEditorOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            height: '90vh',
            width: '95vw',
            maxWidth: '95vw'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FullscreenIcon />
            <Typography variant="h6">Éditeur LaTeX - Plein Écran</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              gap: 0,
              overflow: 'hidden'
            }}
          >
            {/* LaTeX Editor */}
            <Box
              sx={{
                width: `${editorWidth}%`,
                display: 'flex',
                flexDirection: 'column',
                minWidth: '20%',
                maxWidth: '80%'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Template LaTeX
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <LatexEditor
                  value={latexTemplate}
                  onChange={(newValue) => {
                    if (selectedPattern && (!selectedPattern.is_public || isUserAuthor(selectedPattern))) {
                      setLatexTemplate(newValue);
                    }
                  }}
                  height="calc(90vh - 180px)"
                  disabled={!selectedPattern || (selectedPattern.is_public && !isUserAuthor(selectedPattern))}
                />
              </Box>
            </Box>

            {/* Resizable Divider */}
            <Box
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = editorWidth;
                const dialogElement = (e.currentTarget as HTMLElement).closest('[role="dialog"]') as HTMLElement;
                const dialogWidth = dialogElement?.clientWidth || 1000;

                const handleMouseMove = (moveEvent: MouseEvent) => {
                  const deltaX = moveEvent.clientX - startX;
                  const deltaPercent = (deltaX / dialogWidth) * 100;
                  const newWidth = Math.min(80, Math.max(20, startWidth + deltaPercent));
                  setEditorWidth(newWidth);
                };

                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
              sx={{
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                transition: 'background-color 0.2s',
                flexShrink: 0
              }}
            />

            {/* PDF Preview */}
            <Box
              sx={{
                width: `${100 - editorWidth}%`,
                display: 'flex',
                flexDirection: 'column',
                minWidth: '20%',
                maxWidth: '80%',
                pl: 2
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Aperçu PDF
                </Typography>
                <Button
                  size="small"
                  onClick={handlePreview}
                  variant="contained"
                  disabled={!selectedPattern}
                  startIcon={<PreviewIcon />}
                >
                  Générer
                </Button>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  flexGrow: 1,
                  p: 1,
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5'
                }}
              >
                {showPdfPreview && selectedPattern ? (
                  <PatternPdfViewer
                    key={pdfPreviewKey}
                    latexTemplate={latexTemplate}
                    pythonGenerator={pythonGenerator}
                    isReusable={isReusable}
                    iterations={isReusable ? 3 : 1}
                    patternId={selectedPattern?.id}
                    onRetryWithAiLatex={handleRetryWithAiLatex}
                    onRetryWithAiPython={handleRetryWithAiPython}
                    onPdfGenerated={async () => {
                      if (selectedPattern?.id) {
                        try {
                          const updatedPattern = await patternService.getPattern(selectedPattern.id);
                          if (updatedPattern) {
                            setSelectedPattern(updatedPattern);
                          }
                        } catch (error) {
                          console.error('Error reloading pattern after PDF generation:', error);
                        }
                      }
                    }}
                  />
                ) : selectedPattern?.preview_pdf_url ? (
                  <PdfDisplay
                    url={selectedPattern.preview_pdf_url}
                    title="Aperçu du pattern"
                  />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      height: '100%',
                      p: 3
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Cliquez sur "Générer" pour créer un aperçu PDF
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullscreenLatexEditorOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Solution Editing Modal */}
      <Dialog
        open={editSolutionModalOpen}
        onClose={handleCloseSolutionModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: selectedSolution?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Solution pour : {selectedSolution?.pattern_title}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={selectedSolution?.is_public || false}
                    onChange={(e) => handleToggleSolutionPublic(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {selectedSolution?.is_public ? "Public" : "Privé"}
                  </Typography>
                }
                labelPlacement="start"
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteSolutionFromModal}
                size="small"
              >
                Supprimer
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{
          backgroundColor: selectedSolution?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
        }}>
          {selectedSolution && (
            <Grid container spacing={2}>
              {/* Savoir-faire (toute la largeur) */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    Savoir-faire de la solution
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSkillSelectionModalOpen(true)}
                  >
                    Sélectionner les savoir-faire ({skillsWithWeights.length})
                  </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {skillsWithWeights.length > 0 ? (
                    skillsWithWeights.map((sw) => {
                      const skill = allSkills.find(s => s.id === sw.skill_id);
                      return skill ? (
                        <Chip
                          key={skill.id}
                          label={`${skill.name} (poids: ${sw.weight})`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : null;
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Aucun savoir-faire sélectionné
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Template LaTeX à gauche */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Template LaTeX
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  value={solutionLatexTemplate}
                  onChange={(e) => setSolutionLatexTemplate(e.target.value)}
                  placeholder="Entrez le template LaTeX de la solution..."
                  helperText="Utilisez {{variable}} pour les valeurs dynamiques du pattern"
                  variant="outlined"
                  sx={{
                    fontFamily: 'monospace',
                    '& .MuiInputBase-root': {
                      height: '400px',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      height: '100% !important',
                      overflow: 'auto !important',
                      maxHeight: '100%',
                    },
                    '& textarea': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    }
                  }}
                />
              </Grid>

              {/* Aperçu PDF à droite */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Aperçu PDF
                </Typography>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleVisualizeSolution}
                    disabled={loading}
                  >
                    {loading ? 'Génération...' : 'Visualiser la solution'}
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, height: 'calc(400px - 36px)', overflow: 'auto' }}>
                  <PdfViewer
                    key={solutionPdfKey}
                    url={`/solutions/${selectedSolution.id}/compile_pdf/?t=${solutionPdfKey}`}
                    title={`solution_${selectedSolution.id}`}
                  />
                </Paper>
              </Grid>

              {/* Fonction Python en dessous */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Fonction Python (optionnel)
                </Typography>
                <PythonEditor
                  value={solutionPythonFunction}
                  onChange={(value) => setSolutionPythonFunction(value)}
                  placeholder="Entrez une fonction Python optionnelle pour des calculs supplémentaires..."
                  helperText="Cette fonction peut être utilisée pour des transformations ou calculs"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '300px',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      height: '100% !important',
                      overflow: 'auto !important',
                      maxHeight: '100%',
                    },
                    '& textarea': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    }
                  }}
                />
              </Grid>

              {/* Tags */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Tags</Typography>
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    {solutionTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleRemoveSolutionTag(tag)}
                        size="small"
                      />
                    ))}
                  </Box>
                  <Box display="flex" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Ajouter un tag"
                      value={solutionTagInput}
                      onChange={(e) => setSolutionTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSolutionTag()}
                    />
                    <Button size="small" onClick={handleAddSolutionTag}>
                      Ajouter
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Valeurs générées par la solution */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    Valeurs finales générées par la solution
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRegenerateSolutionValues}
                    disabled={loading}
                  >
                    {loading ? 'Génération...' : 'Générer'}
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, height: '300px', overflow: 'auto', bgcolor: '#f5f5f5' }}>
                  {selectedSolution?.generated_values ? (
                    <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedSolution.generated_values, null, 2)}
                    </pre>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune valeur générée. Cliquez sur "Générer" pour générer des valeurs.
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Valeurs générées par le pattern */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    Valeurs générées par le pattern
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRegeneratePatternValues}
                    disabled={loading}
                  >
                    {loading ? 'Génération...' : 'Re-générer'}
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, height: '300px', overflow: 'auto', bgcolor: '#f5f5f5' }}>
                  {selectedSolution?.pattern_generated_values ? (
                    <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedSolution.pattern_generated_values, null, 2)}
                    </pre>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune valeur générée. Cliquez sur "Re-générer" pour générer des valeurs.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{
          backgroundColor: selectedSolution?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
        }}>
          <Button onClick={handleCloseSolutionModal} variant="contained">
            Fermer et Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skill Selection Modal */}
      <SkillSelectionModal
        open={skillSelectionModalOpen}
        onClose={() => setSkillSelectionModalOpen(false)}
        onSave={handleSaveSkillsWithWeights}
        initialSkills={skillsWithWeights}
      />

      {/* Tree View Modal */}
      <Dialog
        open={treeViewOpen}
        onClose={() => setTreeViewOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 0,
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <TreeIcon />
                <Typography variant="h6">Arborescence des patterns</Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  onClick={() => mindMapRef.current?.collapseAll()}
                  variant="outlined"
                  size="small"
                >
                  Fermer tous les dossiers
                </Button>
                <Button
                  onClick={() => {
                    mindMapRef.current?.enableExpandMode();
                    setIsExpandModeActive(true);
                    setIsCollapseSiblingsModeActive(false);
                  }}
                  variant={isExpandModeActive ? "contained" : "outlined"}
                  size="small"
                  color={isExpandModeActive ? "success" : "primary"}
                >
                  Ouvrir tous les noeuds fils
                </Button>
                <Button
                  onClick={() => {
                    mindMapRef.current?.enableCollapseSiblingsMode();
                    setIsCollapseSiblingsModeActive(true);
                    setIsExpandModeActive(false);
                  }}
                  variant={isCollapseSiblingsModeActive ? "contained" : "outlined"}
                  size="small"
                  color={isCollapseSiblingsModeActive ? "warning" : "primary"}
                >
                  Fermer les noeuds frères
                </Button>
                <Button onClick={() => setTreeViewOpen(false)} variant="outlined">
                  Fermer
                </Button>
              </Box>
            </Box>
            <TextField
              size="small"
              placeholder="Rechercher un pattern ou container..."
              value={treeSearchQuery}
              onChange={(e) => setTreeSearchQuery(e.target.value)}
              fullWidth
              sx={{ maxWidth: '400px' }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: 'calc(90vh - 160px)' }}>
          <PatternMindMap
            ref={mindMapRef}
            patterns={allPatternsForTree}
            containers={allContainersForTree}
            searchQuery={debouncedTreeSearchQuery}
            onPatternClick={(pattern) => {
              handlePatternSelect(pattern, false);
              setTreeViewOpen(false);
            }}
            onContainerClick={(container) => {
              handlePatternSelect(container, true);
              setTreeViewOpen(false);
            }}
            onExpandModeChange={setIsExpandModeActive}
            onCollapseSiblingsModeChange={setIsCollapseSiblingsModeActive}
          />
        </DialogContent>
      </Dialog>

      {/* Quick Recap Modal */}
      <QuickRecapModal
        open={quickRecapModalOpen}
        onClose={() => setQuickRecapModalOpen(false)}
        itemTitle={quickRecapPattern?.title || ''}
        courses={courses}
        videos={videos}
        selectedCourseId={quickRecapPattern?.quick_recap_course}
        selectedVideoId={quickRecapPattern?.quick_recap_video}
        onSaveCourse={handleSaveQuickRecapCourse}
        onSaveVideo={handleSaveQuickRecapVideo}
      />

      {/* Composite Pattern Tree Picker */}
      <Dialog
        open={compositeTreeOpen}
        onClose={() => setCompositeTreeOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { width: '90vw', height: '90vh', maxWidth: '90vw', maxHeight: '90vh', m: 0 } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <TreeIcon color="secondary" />
              <Typography variant="h6">Sélectionner un pattern</Typography>
            </Box>
            <Button onClick={() => setCompositeTreeOpen(false)} variant="outlined">Fermer</Button>
          </Box>
          <TextField
            size="small"
            placeholder="Rechercher..."
            value={compositeTreeSearch}
            onChange={(e) => setCompositeTreeSearch(e.target.value)}
            sx={{ mt: 1, maxWidth: 400 }}
            fullWidth
          />
        </DialogTitle>
        <DialogContent dividers sx={{ height: 'calc(90vh - 160px)' }}>
          <PatternMindMap
            ref={compositeTreeRef}
            patterns={compositeTreePatterns}
            containers={compositeTreeContainers}
            searchQuery={compositeTreeSearch}
            onPatternClick={handleCompositeTreeSelect}
            onContainerClick={() => {}}
            onExpandModeChange={() => {}}
            onCollapseSiblingsModeChange={() => {}}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ExercisePatterns;